/**
 * Modular Periodic Table Application
 * ES6+ component-driven architecture for optimal performance and maintainability
 */

// ============================================================================
// CONFIGURATION CONSTANTS
// ============================================================================

class AppConfig {
  static SCROLL_THRESHOLD = 800; // px before back-to-top button appears
  static MOBILE_BREAKPOINT = 576; // px for media query
  static MAX_F_BLOCK_ELEMENTS = 15; // lanthanides and actinides
  static DEFAULT_LOCALE = 'en_US';
  static API_TIMEOUT = 10000; // ms
}

// ============================================================================
// TRANSLATION MANAGER - Loads UI/content strings with locale fallback
// ============================================================================
class TranslationManager {
  constructor() {
    // Supported language codes that have corresponding translation data files
    this.supportedLanguages = ['en', 'de', 'fr', 'es', 'it', 'da', 'sv'];
    this.language = this.getLanguagePart(LocaleUtils.getLocaleCookie());
    this.basePath = '/marketing-ui/periodic-table/static/data';
    this.fallbackFile = 'general_content_en.json';
    this.data = {};
  }

  getLanguagePart(locale) {
    if (!locale) return 'en';
    const langPart = String(locale).split('_')[0] || 'en';
    
    // Validate against supported languages and fallback to 'en' if not supported
    return this.supportedLanguages.includes(langPart) ? langPart : 'en';
  }

  async load() {
    const primaryUrl = `${this.basePath}/general_content_${this.language}.json`;
    const fallbackUrl = `${this.basePath}/${this.fallbackFile}`;
    try {
      this.data = await this.fetchJSON(primaryUrl);
    } catch (e) {
  console.warn('Translation file not found for selected language; falling back to default');
      try {
        this.data = await this.fetchJSON(fallbackUrl);
      } catch (fallbackErr) {
  console.error('Failed to load fallback translation file');
        this.data = {};
      }
    }
  }

  async fetchJSON(url) {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  t(key, fallback = '') {
    if (!key) return fallback || key;
    const parts = key.split('.');
    let cursor = this.data;
    for (const p of parts) {
      if (cursor && Object.prototype.hasOwnProperty.call(cursor, p)) {
        cursor = cursor[p];
      } else {
        return fallback || key;
      }
    }
    return typeof cursor === 'string' ? cursor : fallback || key;
  }

  format(key, replacements = {}, fallback = '') {
    const raw = this.t(key, fallback);
    return raw.replace(/\{(.*?)\}/g, (m, p1) => (p1 in replacements ? replacements[p1] : m));
  }

  /**
   * Translate element property values (type, subtype, state) for display
   * Maps English values to translated secondary filter labels
   * @param {string} value - The English property value (e.g., "Alkali Metals", "Gases")
   * @param {string} propertyType - The type of property: 'type', 'subtype', or 'state'
   * @returns {string} - Translated value or original if no translation found
   */
  translateElementProperty(value, propertyType = 'subtype') {
    if (!value) return value;
    
    // Mapping of English values to translation keys in filters.secondary
    const translationKeyMap = {
      // Types
      'Metal': 'metals',
      'Nonmetal': 'nonmetals',
      'Metalloid': 'metalloids',
      // Subtypes
      'Alkali Metals': 'alkaliMetals',
      'Alkaline Earth Metals': 'alkalineEarthMetals',
      'Transition Metals': 'transitionMetals',
      'Post-Transition Metals': 'postTransitionMetals',
      'Lanthanides': 'lanthanides',
      'Actinides': 'actinides',
      'Noble Gases': 'nobleGases',
      'Halogens': 'halogens',
      'Nonmetals': 'nonmetals',
      'Metalloid': 'metalloids',
      // States
      'Gases': 'gases',
      'Liquids': 'liquids',
      'Solids': 'solids',
      'Unknown': 'unknown'
    };

    const translationKey = translationKeyMap[value];
    if (translationKey) {
      // Check in filters.secondary first
      const translated = this.t(`filters.secondary.${translationKey}`);
      if (translated && translated !== `filters.secondary.${translationKey}`) {
        return translated;
      }
      // Fallback to filters.primary for main types
      const primaryTranslated = this.t(`filters.primary.${translationKey}`);
      if (primaryTranslated && primaryTranslated !== `filters.primary.${translationKey}`) {
        return primaryTranslated;
      }
    }
    
    return value; // Return original if no translation found
  }

  applyTranslations() {
    const bindText = (selector, key) => {
      const el = document.querySelector(selector);
      if (el) el.textContent = this.t(key, el.textContent);
    };

    const bindAriaLabel = (selector, key) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute('aria-label', this.t(key, el.getAttribute('aria-label')));
    };
    // (Index.html bindings require IDs added there; non-existent selectors are ignored.)
    // Special handling for #pt-header-title: preserve inner HTML (tooltip span) and only replace leading text node
    const headerTitleEl = document.querySelector('#pt-header-title');
    if (headerTitleEl) {
      const translatedHeader = this.t('header.title', headerTitleEl.textContent.trim());
      // Find first text node child
      let textNode = null;
      for (const node of headerTitleEl.childNodes) {
        if (node.nodeType === Node.TEXT_NODE) {
          textNode = node;
          break;
        }
      }
      if (textNode) {
        // Preserve trailing space before span if existed
        textNode.nodeValue = translatedHeader + ' ';
      } else {
        headerTitleEl.insertBefore(document.createTextNode(translatedHeader + ' '), headerTitleEl.firstChild);
      }
    }
    bindText('#pt-header-intro1', 'header.paragraphs.intro1');
    bindText('#pt-header-intro2', 'header.paragraphs.intro2');
    // Reconstruct intro3 with printable link placeholder
    const intro3El = document.querySelector('#pt-header-intro3');
    const printableLinkEl = document.querySelector('#pt-printable-link');
    if (intro3El && printableLinkEl) {
      const linkText = this.t('header.printableLinkText', printableLinkEl.textContent);
      const linkHtml = `<a id="pt-printable-link" class="kmd-link" href="/marketing-ui/periodic-table/static/resources/fishersci-periodic-table-download.pdf" target="_blank">${linkText}</a>`;
      const full = this.format('header.paragraphs.intro3', { printableLink: linkHtml }, intro3El.textContent);
      // Replace paragraph content with formatted text including anchor
      intro3El.innerHTML = full;
    }
    bindText('#pt-share-prefix', 'share.prefix');
    const shareGroup = document.getElementById('pt-share-group');
    if (shareGroup) shareGroup.setAttribute('aria-label', this.t('share.groupLabel', shareGroup.getAttribute('aria-label')));
    const altMap = [
      ['.pt-share-email img', 'share.emailAria'],
      ['.pt-share-facebook img', 'share.facebookAria'],
      ['.pt-share-linkedin img', 'share.linkedinAria']
    ];
    altMap.forEach(([sel, key]) => { const el = document.querySelector(sel); if (el) el.alt = this.t(key, el.alt); });
    bindText('#pt-filter-by-label', 'filters.filterByLabel');
    bindAriaLabel('#pt-filters-primary', 'filters.filterByLabel');
    bindAriaLabel('#pt-table-container', 'table.ariaLabel');
    const chipMap = { metals: 'filters.primary.metals', metalloids: 'filters.primary.metalloids', nonmetals: 'filters.primary.nonmetals', states: 'filters.primary.states', groups: 'filters.primary.groups', periods: 'filters.primary.periods' };
    Object.entries(chipMap).forEach(([dataAttr, key]) => { const chip = document.querySelector(`core-chip[data-attr='${dataAttr}']`); if (chip) chip.setAttribute('label', this.t(key, chip.getAttribute('label'))); });
    bindText('.pt-reset-all-filters', 'filters.reset');
    bindText('#pt-responsive-symbol-label', 'table.responsive.symbol');
    bindText('#pt-responsive-name-label', 'table.responsive.name');
    bindText('#pt-responsive-atomic-weight-label', 'table.responsive.atomicWeight');
    bindText('#pt-responsive-atomic-number-label', 'table.responsive.atomicNumber');
    bindText('#pt-elements-heading', 'table.elementsHeading');
    const mobileSearch = document.getElementById('pt-table-search');
    if (mobileSearch) mobileSearch.setAttribute('placeholder', this.t('table.searchPlaceholder', mobileSearch.getAttribute('placeholder')));
    const desktopSearch = document.getElementById('pt-core-search');
    if (desktopSearch) desktopSearch.setAttribute('placeholder', this.t('table.searchPlaceholder', desktopSearch.getAttribute('placeholder')));
    const backBtn = document.getElementById('pt-back-to-top');
    if (backBtn) {
      backBtn.setAttribute('label', this.t('buttons.backToTop', backBtn.getAttribute('label')));
      backBtn.setAttribute('arialabel', this.t('buttons.backToTopAria', backBtn.getAttribute('arialabel')));
    }
    bindAriaLabel('#pt-tabular-data-table', 'aria.elementsData');

    // Update core-table column headers asynchronously
    this.updateTableColumnHeaders();

    // Update ledger infographic images based on locale
    this.updateLedgerImages();
  }

  /**
   * Update core-table column headers with translations
   * Called from applyTranslations() to translate table headers in shadow DOM
   */
  async updateTableColumnHeaders() {
    const coreTable = document.getElementById('pt-core-table');
    if (!coreTable) {
      console.log('core-table element not found for column header updates');
      return;
    }

    try {
      // Wait for shadow DOM to be ready
      const shadowRoot = await AccessibilityUtils.waitForShadowDOMElement(coreTable, '.core-table__header', 2000);
      if (!shadowRoot || !coreTable.shadowRoot) {
        console.warn('Table shadow DOM not ready for column header updates');
        return;
      }

      // Define column translations
      const columnTranslations = [
        { field: 'name', translationKey: 'table.columns.elementName', fallback: 'Element Name' },
        { field: 'symbol', translationKey: 'table.columns.symbol', fallback: 'Symbol' },
        { field: 'atomicNumber', translationKey: 'table.columns.atomicNumber', fallback: 'Atomic Number' },
        { field: 'toggle', translationKey: 'table.columns.toggle', fallback: ' ' }
      ];

      // Update each column header with translated text
      for (const col of columnTranslations) {
        const { field, translationKey, fallback } = col;
        if (!field || !translationKey) continue;

        // Get the translated text
        const translatedText = this.t(translationKey, fallback || field);
        
        // Find the header cell for this field using the slot name
        const slotName = `header-${field}`;
        const headerSlot = coreTable.shadowRoot.querySelector(`slot[name="${slotName}"]`);
        
        if (headerSlot) {
          headerSlot.textContent = translatedText;
        }
      }
    } catch (error) {
      console.debug('Unable to update table column headers:', error);
    }
  }

  /**
   * Update ledger infographic images based on locale.
   * Looks for <img> tags whose src contains "pt-ledger_" and swaps to language-specific file.
   * Uses the validated language code from constructor (already checked against supportedLanguages).
   */
  updateLedgerImages() {
    try {
      // Language is already validated in getLanguagePart() during initialization
      const images = document.querySelectorAll('img[src*="pt-ledger_"]');
      if (!images.length) return;

      images.forEach(img => {
        const desiredSrc = `/marketing-ui/periodic-table/static/images/pt-ledger_${this.language}.svg`;
        // If already correct, skip
        if (img.getAttribute('src') === desiredSrc) return;
        // Ensure fallback on load error
        img.onerror = function() {
          this.onerror = null; // prevent loop
          this.src = '/marketing-ui/periodic-table/static/images/pt-ledger_en.svg';
        };
        img.setAttribute('src', desiredSrc);
      });
    } catch (e) {
      console.warn('Failed to update ledger images, using English fallback');
    }
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

class SecurityUtils {
  /**
   * Sanitize HTML to prevent XSS attacks
   * @param {string} html - Raw HTML string
   * @returns {string} Sanitized HTML
   */
  static sanitizeHTML(html) {
    const temp = document.createElement('div');
    temp.textContent = html;
    return temp.innerHTML;
  }

  /**
   * Escape HTML special characters
   * @param {string} str - String to escape
   * @returns {string} Escaped string
   */
  static escapeHTML(str) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#x27;',
      '/': '&#x2F;'
    };
    return String(str).replace(/[&<>"'/]/g, (char) => map[char]);
  }

  /**
   * Validate cookie value format
   * @param {string} value - Cookie value to validate
   * @returns {boolean} True if valid
   */
  static validateCookie(value) {
    return /^[a-zA-Z0-9_-]+$/.test(value);
  }

  /**
   * Validate URL is safe
   * @param {string} url - URL to validate
   * @returns {boolean} True if valid http/https URL
   */
  static validateURL(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  }

  /**
   * Safely set innerHTML with sanitization
   * @param {HTMLElement} element - Target element
   * @param {string} html - HTML content
   */
  static safeSetInnerHTML(element, html) {
    if (!element) return;
    
    // For trusted template content, use directly
    if (html instanceof DocumentFragment || html instanceof Node) {
      element.innerHTML = '';
      element.appendChild(html);
      return;
    }
    
    // For string content, create elements safely
    const temp = document.createElement('div');
    temp.innerHTML = html;
    element.innerHTML = '';
    while (temp.firstChild) {
      element.appendChild(temp.firstChild);
    }
  }
}

class DOMUtils {
  static addClass(element, className) {
    element?.classList.add(className);
  }

  static removeClass(element, className) {
    element?.classList.remove(className);
  }

  static toggleClass(element, className, force) {
    return element?.classList.toggle(className, force);
  }

  static hasClass(element, className) {
    return element?.classList.contains(className) ?? false;
  }

  static hide(element) {
    if (element) {
      element.classList.add('kmd-d-none');
      AccessibilityUtils.setAriaHidden(element, true);
    }
  }

  static show(element) {
    if (element) {
      element.classList.remove('kmd-d-none');
      AccessibilityUtils.setAriaHidden(element, false);
    }
  }
}

class EventBus {
  static dispatch(eventName, detail = {}) {
    const event = new CustomEvent(eventName, { detail, bubbles: true });
    document.dispatchEvent(event);
  }

  static on(eventName, handler) {
    document.addEventListener(eventName, handler);
  }

  static off(eventName, handler) {
    document.removeEventListener(eventName, handler);
  }
}

class SearchUtils {
  /**
   * Search elements by name, symbol, or latin synonyms
   * @param {Array} elements - Array of element objects
   * @param {string} query - Search query
   * @returns {Array} Filtered elements matching query
   */
  static searchElements(elements, query) {
    if (!query || !Array.isArray(elements)) return elements;
    
    const lowerQuery = query.toLowerCase().trim();
    
    return elements.filter(el => {
      // Match by name
      if (el.name?.toLowerCase().includes(lowerQuery)) {
        return true;
      }
      
      // Match by symbol
      if (el.symbol?.toLowerCase().includes(lowerQuery)) {
        return true;
      }
      
      // Match by latin synonyms
      if (Array.isArray(el.synonyms)) {
        return el.synonyms.some(syn => 
          syn?.toLowerCase().includes(lowerQuery)
        );
      }
      
      return false;
    });
  }
}

class VisibilityUtils {
  /**
   * Update element tile visibility based on filter/search results
   * @param {Array} matchedElements - Elements that should be visible
   * @param {boolean} disableAll - If true, disable all elements
   */
  static updateElementVisibility(matchedElements, disableAll = false) {
    const matchSet = new Set(
      Array.isArray(matchedElements) 
        ? matchedElements.map(el => String(el.atomicNumber)).filter(Boolean)
        : []
    );

    document.querySelectorAll('.pt-element-tile').forEach(tile => {
      const z = tile.getAttribute('data-z');
      const invArea = tile.querySelector('.pt-inv-area');
      
      if (!z) {
        // Empty tiles
        if (disableAll) {
          DOMUtils.addClass(tile, 'pt-element-dimmed');
          AccessibilityUtils.updateElementAriaLabel(tile, true);
        }
        return;
      }

      const shouldDim = disableAll || !matchSet.has(z);
      
      if (shouldDim) {
        DOMUtils.addClass(tile, 'pt-element-dimmed');
        if (invArea) DOMUtils.addClass(invArea, 'pt-element-inv-dimmed');
        AccessibilityUtils.updateElementAriaLabel(tile, true);
      } else {
        DOMUtils.removeClass(tile, 'pt-element-dimmed');
        if (invArea) DOMUtils.removeClass(invArea, 'pt-element-inv-dimmed');
        AccessibilityUtils.updateElementAriaLabel(tile, false);
      }
    });
  }

  /**
   * Show all elements (remove disabled state)
   */
  static showAllElements() {
    document.querySelectorAll('.pt-element-tile').forEach(tile => {
      DOMUtils.removeClass(tile, 'pt-element-dimmed');
      const invArea = tile.querySelector('.pt-inv-area');
      if (invArea) DOMUtils.removeClass(invArea, 'pt-element-inv-dimmed');
      AccessibilityUtils.updateElementAriaLabel(tile, false);
    });
  }

  /**
   * Disable all elements
   */
  static disableAllElements() {
    VisibilityUtils.updateElementVisibility([], true);
  }
}

class LocaleUtils {
  /**
   * Get the locale cookie value
   * @returns {string} The locale cookie value (e.g., "fr_FR") or default "us_en"
   */
  static getLocaleCookie() {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'locale') {
        // Validate cookie value before using
        if (SecurityUtils.validateCookie(value)) {
          return value;
        }
        console.warn('Invalid locale cookie value detected');
      }
    }
    return AppConfig.DEFAULT_LOCALE;
  }

  /**
   * Parse the locale cookie into country and language
   * @param {string} locale - The locale string (e.g., "fr_FR")
   * @returns {{country: string, language: string}} Object with country and language
   */
  static parseLocale(locale) {
    const [language, countryCode] = locale.split('_');
    return {
      language: language ? language.toLowerCase() : 'en',
      country: countryCode ? countryCode.toLowerCase() : 'us'
    };
  }

  /**
   * Build a localized URL from a relative path
   * @param {string} path - The relative path (e.g., "/products/I9C8K3FF/phosphorus.html?nav=25352")
   * @returns {string} The full localized URL (e.g., "/us/en/products/I9C8K3FF/phosphorus.html?nav=25352")
   */
  static buildLocalizedUrl(path) {
    if (!path) return '#';

    // If path starts with http:// or https://, validate and return
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return SecurityUtils.validateURL(path) ? path : '#';
    }

    const locale = this.getLocaleCookie();
    const { country, language } = this.parseLocale(locale);

    // Remove leading slash if present to avoid double slashes
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;

    // Build the URL: /{country}/{language}/{path}
    return `/${country}/${language}/${cleanPath}`;
  }
}

class AccessibilityUtils {
  // ============================================================================
  // ARIA ATTRIBUTE MANAGEMENT
  // ============================================================================

  /**
   * Make element symbols aria-readable
   * @param {string} symbol - Element symbol
   * @returns {string} Aria-readable string
   */
  static makeAriaReadable(symbol) {
    return symbol.replace(/([A-Z])([a-z])/g, '$1 $2');
  }

  /**
   * Set aria-hidden attribute on an element
   * @param {HTMLElement} element - The element to update
   * @param {boolean} hidden - Whether the element should be hidden from screen readers
   */
  static setAriaHidden(element, hidden) {
    if (!element) return;
    
    if (hidden) {
      element.setAttribute('aria-hidden', 'true');
    } else {
      element.removeAttribute('aria-hidden');
    }
  }

  /**
   * Update aria-label for dimmed state
   * @param {HTMLElement} tile - The tile element
   * @param {boolean} isDimmed - Whether to add or remove dimmed state
   */
  static updateElementAriaLabel(tile, isDimmed) {
    if (!tile) return;
    
    const currentLabel = tile.getAttribute('aria-label');
    if (!currentLabel) return;
    
    let dimmedText = 'dimmed';
    if(window.ptTranslations) { 
      dimmedText = window.ptTranslations.t('aria.dimmedSuffix', 'dimmed');
    }

    if (isDimmed && !currentLabel.includes(dimmedText)) {
      tile.setAttribute('aria-label', `${currentLabel}, ${dimmedText}`);
    } else if (!isDimmed && currentLabel.includes(dimmedText)) {
      tile.setAttribute('aria-label', currentLabel.replace(`, ${dimmedText}`, ''));
    }
  }

  /**
   * Set aria-expanded attribute on an element
   * @param {HTMLElement} element - The element to update
   * @param {boolean} expanded - Whether the element is expanded
   */
  static setAriaExpanded(element, expanded) {
    if (!element) return;
    element.setAttribute('aria-expanded', String(expanded));
  }

  /**
   * Set aria-controls attribute on an element
   * @param {HTMLElement} element - The element to update
   * @param {string} controlsId - The ID of the controlled element
   */
  static setAriaControls(element, controlsId) {
    if (!element) return;
    element.setAttribute('aria-controls', controlsId);
  }

  /**
   * Set aria-label attribute on an element
   * @param {HTMLElement} element - The element to update
   * @param {string} label - The accessible label
   */
  static setAriaLabel(element, label) {
    if (!element) return;
    element.setAttribute('aria-label', label);
  }

  /**
   * Update button aria-label based on expanded state
   * @param {HTMLElement} button - The button element
   * @param {boolean} isExpanded - Whether the button controls expanded content
   * @param {string} elementName - Name of the element being controlled
   * @param {string} elementSymbol - Symbol of the element (optional)
   * @param {number} atomicNumber - Atomic number of the element (optional)
   */
  static updateButtonAriaLabel(button, isExpanded, elementName, elementSymbol = '', atomicNumber = null) {
    if (!button || !elementName) return;

    const state = isExpanded ? (window.ptTranslations ? window.ptTranslations.t('buttons.expandDetailsAction', 'Expanded') : 'Expanded') : (window.ptTranslations ? window.ptTranslations.t('buttons.collapseDetailsAction', 'Collapsed') : 'Collapsed');
    const symbolPart = elementSymbol ? `, ` + (window.ptTranslations ? window.ptTranslations.t('aria.symbolLabel', 'Symbol') : 'Symbol') + `: (${this.makeAriaReadable(elementSymbol)})` : '';
    const numberPart = atomicNumber ? `, ` + (window.ptTranslations ? window.ptTranslations.t('aria.atomicNumberLabel', 'Atomic Number') : 'Atomic Number') + `: ${atomicNumber}` : '';

    const ariaLabel = `${elementName}${symbolPart}${numberPart}, button, ${state}`;
    button.setAttribute('aria-label', ariaLabel);
    
    // Update title attribute for mouse users
    const action = isExpanded ? 'Collapse' : 'Expand';
    button.title = `${action} details for ${elementName}`;
  }

  // ============================================================================
  // FOCUS MANAGEMENT
  // ============================================================================

  /**
   * Restore focus to a specific element
   * @param {HTMLElement} element - The element to focus
   * @param {number} delay - Optional delay in milliseconds before focusing
   */
  static restoreFocus(element, delay = 50) {
    if (!element || !document.contains(element)) return;
    
    setTimeout(() => {
      element.focus();
    }, delay);
  }

  /**
   * Set focus on a modal element with proper accessibility support
   * @param {HTMLElement} modal - The modal element to focus
   * @param {string} modalHeader - The header text for screen reader announcement
   */
  static setModalFocus(modal, modalHeader = 'Element Details') {
    if (!modal) return;
    if (window.ptTranslations) {
      modalHeader = window.ptTranslations.t('modal.defaultHeader', modalHeader);
    }
    
    // Blur the currently focused element first to release focus
    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }
    
    // Create an assertive ARIA live region announcement for screen readers
    // This ensures the announcement is prioritized over modal content
  const announcement = window.ptTranslations ? window.ptTranslations.format('modal.openedAnnouncement', { modalHeader }, `${modalHeader} dialog opened. Press Tab to navigate to the close button, or press Escape to close.`) : `${modalHeader} dialog opened. Press Tab to navigate to the close button, or press Escape to close.`;
    this.announceModalToScreenReader(announcement);
    
    // Delay focus slightly to allow screen reader to announce the dialog first
    setTimeout(() => {
      // Make modal container focusable and focus it
      // Tab order: modal container (focused) → close button in shadow DOM → content in light DOM
      modal.setAttribute('tabindex', '-1');
      modal.focus();
    }, 100);
  }

  /**
   * Announce modal opening to screen readers with assertive priority
   * @param {string} message - The message to announce
   */
  static announceModalToScreenReader(message) {
    if (!message) return;
    
    let liveRegion = document.getElementById('pt-modal-live-region');
    
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'pt-modal-live-region';
      liveRegion.setAttribute('role', 'alert');
      liveRegion.setAttribute('aria-live', 'assertive');
      liveRegion.setAttribute('aria-atomic', 'true');
      // Visually hidden but accessible to screen readers
      liveRegion.style.cssText = 'position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0;';
      document.body.appendChild(liveRegion);
    }
    
    // Clear first, then announce (helps screen readers recognize the change)
    liveRegion.textContent = '';
    
    // Use requestAnimationFrame to ensure the clear happens before the announcement
    requestAnimationFrame(() => {
      liveRegion.textContent = message;
      
      // Keep the message longer (3 seconds) to ensure screen readers have time to read it
      setTimeout(() => {
        liveRegion.textContent = '';
      }, 3000);
    });
  }

  /**
   * Shift focus to the top of the page (typically main content area)
   * @param {HTMLElement} targetElement - Optional specific element to focus
   */
  static shiftFocusToTop(targetElement = null) {
    // Get the target element or find main content area
    const mainElement = targetElement || 
                        document.querySelector('periodic-table-main') || 
                        document.getElementById('main') ||
                        document.querySelector('[role="main"]') ||
                        document.querySelector('h1');
    
    if (!mainElement) return;
    
    // Make the element focusable if it isn't already
    const hadTabIndex = mainElement.hasAttribute('tabindex');
    const oldTabIndex = mainElement.getAttribute('tabindex');
    
    if (!hadTabIndex || oldTabIndex === null) {
      mainElement.setAttribute('tabindex', '-1');
    }
    
    // Focus the element
    mainElement.focus();
    
    // Remove the tabindex we added (restore original state)
    if (!hadTabIndex) {
      mainElement.removeAttribute('tabindex');
    }
  }

  // ============================================================================
  // SCREEN READER ANNOUNCEMENTS
  // ============================================================================

  /**
   * Create or get an ARIA live region for screen reader announcements
   * @param {string} id - The ID for the live region element
   * @returns {HTMLElement} The live region element
   */
  static getOrCreateLiveRegion(id) {
    let liveRegion = document.getElementById(id);
    
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = id;
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      // Visually hidden but accessible to screen readers
      liveRegion.style.cssText = 'position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0;';
      document.body.appendChild(liveRegion);
    }
    
    return liveRegion;
  }

  /**
   * Announce a message to screen readers using ARIA live region
   * @param {string} message - The message to announce
   * @param {string} regionId - The ID for the live region (default: 'pt-sr-announcement')
   * @param {number} clearDelay - Milliseconds to wait before clearing the announcement (default: 1000)
   */
  static announceToScreenReader(message, regionId = 'pt-sr-announcement', clearDelay = 1000) {
    if (!message) return;
    
    const liveRegion = this.getOrCreateLiveRegion(regionId);
    
    // Update and announce the message
    liveRegion.textContent = message;
    
    // Clear the live region after announcement to avoid repeat announcements
    if (clearDelay > 0) {
      setTimeout(() => {
        liveRegion.textContent = '';
      }, clearDelay);
    }
  }

  /**
   * Announce that the page has scrolled to top
   */
  static announceScrollToTop() {
    const msg = window.ptTranslations ? window.ptTranslations.t('aria.pageScrolledTop', 'Page scrolled to top.') : 'Page scrolled to top.';
    this.announceToScreenReader(msg, 'pt-scroll-live-region');
  }

  // ============================================================================
  // KEYBOARD INTERACTION HELPERS
  // ============================================================================

  /**
   * Check if a keyboard event is an activation key (Enter or Space)
   * @param {KeyboardEvent} event - The keyboard event
   * @returns {boolean} True if the key is Enter or Space
   */
  static isActivationKey(event) {
    return event.key === 'Enter' || event.key === ' ';
  }

  /**
   * Add keyboard activation handler to an element (Enter/Space triggers callback)
   * @param {HTMLElement} element - The element to add the handler to
   * @param {Function} callback - The function to call on activation
   */
  static addKeyboardActivation(element, callback) {
    if (!element || typeof callback !== 'function') return;
    
    element.addEventListener('keydown', (e) => {
      if (this.isActivationKey(e)) {
        e.preventDefault();
        callback(e);
      }
    });
  }

  // ============================================================================
  // SHADOW DOM ACCESSIBILITY HELPERS
  // ============================================================================

  /**
   * Wait for shadow DOM to be ready and element to be available
   * @param {HTMLElement} component - The web component element
   * @param {string} selector - CSS selector for the element within shadow DOM
   * @param {number} timeout - Maximum time to wait in milliseconds (default: 2000)
   * @returns {Promise<HTMLElement|null>} The found element or null if timeout
   */
  static async waitForShadowDOMElement(component, selector, timeout = 2000) {
    if (!component) return null;

    const startTime = Date.now();
    
    while (Date.now() - startTime < timeout) {
      // Check if shadow root exists
      if (component.shadowRoot) {
        const element = component.shadowRoot.querySelector(selector);
        if (element) {
          return element;
        }
      }
      
      // Wait a bit before checking again (non-blocking)
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    
    return null; // Timeout reached
  }

  /**
   * Set attributes on an element within a component's shadow DOM
   * Generic method for accessing and updating shadow DOM elements for accessibility
   * @param {HTMLElement} component - The web component element with shadow DOM
   * @param {string} selector - CSS selector for the element within shadow DOM
   * @param {Object} attributes - Key-value pairs of attributes to set (e.g., { 'aria-label': 'Close', 'title': 'Close Modal' })
   * @param {string} componentName - Optional component name for error messages (default: 'component')
   */
  static async setShadowDOMAttributes(component, selector, attributes = {}, componentName = 'component') {
    if (!component) {
      console.warn(`Component is null or undefined: ${componentName}`);
      return;
    }
    
    try {
      // Wait for shadow DOM element to be available (non-blocking)
      const element = await this.waitForShadowDOMElement(component, selector);
      
      if (element) {
        Object.entries(attributes).forEach(([attr, value]) => {
          if (value !== null && value !== undefined) {
            element.setAttribute(attr, String(value));
          }
        });
      } else {
        console.warn(`Element not found in shadow DOM with selector "${selector}" in ${componentName} (timeout)`);
      }
    } catch (error) {
      console.warn(`Unable to set attributes on shadow DOM element in ${componentName}:`, error);
    }
  }

  /**
   * Set aria-label on the clear button in a search component's shadow DOM
   * @param {HTMLElement} searchInput - The core-search component element
   * @param {string} label - The aria-label text (default: 'clear search')
   */
  static setSearchClearButtonAriaLabel(searchInput, label = 'Clear Search') {
    if (window.ptTranslations) {
      label = window.ptTranslations.t('buttons.clearSearch', label);
    }
    const capitalizedLabel = label.charAt(0).toUpperCase() + label.slice(1);
    this.setShadowDOMAttributes(
      searchInput,
      '.core-search__clear',
      {
        'aria-label': label,
        'role': 'button'
      },
      'core-search'
    );
  }

  /**
   * Set aria-label on the close button in a modal component's shadow DOM
   * @param {HTMLElement} modal - The core-modal component element
   * @param {string} label - The aria-label text (default: 'Close Modal')
   */
  static setModalCloseButtonAriaLabel(modal, label = 'Close Modal') {
    if (window.ptTranslations) {
      label = window.ptTranslations.t('buttons.closeModal', label);
    }
    this.setShadowDOMAttributes(
      modal,
      '.core-modal__close',
      {
        'aria-label': label
      },
      'core-modal'
    );
  }

  /**
   * Update aria-label for a core-chip based on its selection state
   * If the chip has 'core-chip--selected' class in shadow DOM, appends ', selected' to the label
   * @param {HTMLElement} chip - The core-chip element
   */
  static async updateChipAriaLabel(chip) {
    if (!chip) return;
    
    try {
      // Check if the chip has the selected attribute or property
      let isSelected = chip.selected; 
      // Get the label text from the chip
      const labelText = chip.getAttribute('label') || chip.textContent?.trim() || '';
      
      if (!labelText) return;
      
      const selectedText = (window.ptTranslations ? window.ptTranslations.t('aria.selected', 'Selected') : 'Selected');
      // Set aria-label: append ', selected' if the chip is selected
      const ariaLabel = isSelected ? `${labelText}, ${selectedText}` : labelText;
      
      // Set it in shadow DOM asynchronously (non-blocking)
      await this.setShadowDOMAttributes(
        chip,
        '.core-chip--selectable',
        {
          'aria-label': ariaLabel,
          'role': 'listitem'
        },
        'core-chip'
      );
    } catch (error) {
      console.debug('Unable to update chip aria-label:', error);
    }
  }

  /**
   * Update aria-labels for all core-chip elements in a container
   * @param {HTMLElement} container - The container element with core-chip children
   */
  static updateAllChipAriaLabels(container) {
    if (!container) return;
    
    const chips = container.querySelectorAll('core-chip');
    // Update all chips in parallel (non-blocking)
    chips.forEach(chip => this.updateChipAriaLabel(chip));
  }
}

// ============================================================================
// DATA LOADER - Handles all API calls and data fetching
// ============================================================================

class DataLoader {
  constructor(translationManager) {
    this.translationManager = translationManager;
    const language = this.translationManager ? this.translationManager.language : 'en';
    
    this.urls = {
      elements: '/marketing-ui/periodic-table/static/data/elements-118.json', // Base file with all element data
      elementNames: `/marketing-ui/periodic-table/static/data/element-names_${language}.json`, // Translated element names
      details: '/marketing-ui/periodic-table/static/data/elements-details.json', // Base file with all details data
      detailsText: `/marketing-ui/periodic-table/static/data/element-details-text_${language}.json`, // Translated descriptions and product links
      infographic: `/marketing-ui/periodic-table/static/data/filters-descriptions_${language}.json`,
      infographicFallback: '/marketing-ui/periodic-table/static/data/filters-descriptions_en.json' // Fallback to English
    };
    
    this.cache = {
      elements: [],
      details: {},
      infographic: {}
    };
  }

  async fetchJSON(url) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch translation resource');
      throw error;
    }
  }

  async loadAll() {
    try {
      // Load base elements file (always the same, contains all element data with English names)
      const elements = await this.fetchJSON(this.urls.elements);

      // Try to load translated element names if not English
      const language = this.translationManager ? this.translationManager.language : 'en';
      if (language !== 'en') {
        try {
          const elementNames = await this.fetchJSON(this.urls.elementNames);
          // Merge translated names into elements array
          elements.forEach(element => {
            if (elementNames[element.symbol]) {
              element.name = elementNames[element.symbol];
            }
          });
          console.debug(`Applied ${language} element name translations`);
        } catch (error) {
          console.warn('Element names translation file not found; using English names');
        }
      }

      // Load base element details file
      const details = await this.fetchJSON(this.urls.details);

      // Try to load translated element details text (descriptions and product links)
      try {
        const detailsText = await this.fetchJSON(this.urls.detailsText);
        // Merge translated descriptions and product link text into details
        Object.keys(details).forEach(atomicNumber => {
          if (detailsText[atomicNumber]) {
            // Merge description
            if (detailsText[atomicNumber].description) {
              details[atomicNumber].description = detailsText[atomicNumber].description;
            }
            // Merge product link text and category, keep href from base file
            if (detailsText[atomicNumber].productLinks && details[atomicNumber].productLinks) {
              details[atomicNumber].productLinks.forEach((link, index) => {
                if (detailsText[atomicNumber].productLinks[index]) {
                  link.text = detailsText[atomicNumber].productLinks[index].text;
                  link.category = detailsText[atomicNumber].productLinks[index].category;
                }
              });
            }
          }
        });
        console.debug(`Applied ${language} element details text translations`);
      } catch (error) {
        console.warn('Element details text file not found; using English text');
      }

      // Try to load locale-specific infographic file first, then fall back to default
      let infographic;
      try {
        infographic = await this.fetchJSON(this.urls.infographic);
      } catch (error) {
        console.warn('Locale-specific infographic file not found; falling back to default');
        infographic = await this.fetchJSON(this.urls.infographicFallback);
        console.debug('Loaded fallback infographic keys in English');
      }

      this.cache.elements = Array.isArray(elements) ? elements : [];
      this.cache.details = details || {};
      this.cache.infographic = infographic || {};

      // Dispatch events for loaded data
      EventBus.dispatch('pt:data-loaded', { count: this.cache.elements.length });
      EventBus.dispatch('pt:details-loaded', { count: Object.keys(this.cache.details).length });
      EventBus.dispatch('pt:infographic-loaded', { count: Object.keys(this.cache.infographic).length });

      return this.cache;
    } catch (error) {
      console.error('Failed to load data');
      throw error;
    }
  }

  getElements() {
    return [...this.cache.elements];
  }

  getDetails() {
    return { ...this.cache.details };
  }

  getInfographic() {
    return { ...this.cache.infographic };
  }

}

// ============================================================================
// TABLE RENDERER - Handles periodic table grid rendering
// ============================================================================

class TableRenderer {
  constructor(dataLoader) {
    this.dataLoader = dataLoader;
    this.container = document.getElementById('pt-data-container');
    this.template = document.getElementById('pt-element-template');
  }

  /**
   * Create element tile from template
   */
  createElementTile(element) {
    if (!this.template?.content) {
      return this.createFallbackTile(element);
    }

    const tile = this.template.content.firstElementChild.cloneNode(true);
    tile.classList.add('pt-element-tile');
    tile.setAttribute('role', 'button');
    tile.setAttribute('tabindex', '0');

    // Populate content areas
    this.populateTileContent(tile, element);
    this.setTileAttributes(tile, element);
    this.addTileClasses(tile, element);

    return tile;
  }

  populateTileContent(tile, element) {
    const contentMap = {
      '.pt-symbol': element.symbol,
      '.pt-symbol-lg': element.symbol,
      '.pt-symbol-md': element.symbol,
      '.pt-atomic-number': element.atomicNumber,
      '.pt-atomic-number-lg': element.atomicNumber,
      '.pt-atomic-number-md': element.atomicNumber,
      '.pt-name': element.name,
      '.pt-name-md': element.name,
      '.pt-atomic-weight': element.atomicWeight,
      '.pt-atomic-weight-md': element.atomicWeight
    };

    Object.entries(contentMap).forEach(([selector, value]) => {
      const el = tile.querySelector(selector);
      if (el && value) {
        el.textContent = String(value);
      }
    });
  }

  setTileAttributes(tile, element) {
    // Create descriptive aria-label for screen readers
    const symbol = AccessibilityUtils.makeAriaReadable(element.symbol);
    const name = element.name || '';
    const atomicNumber = element.atomicNumber || '';
    const atomicWeight = element.atomicWeight || '';

    const symbolText = window.ptTranslations ? window.ptTranslations.t('aria.symbolLabel', 'Symbol') : 'Symbol';
    const atomicNumberText = window.ptTranslations ? window.ptTranslations.t('aria.atomicNumberLabel', 'Atomic Number') : 'Atomic Number';
    const atomicWeightText = window.ptTranslations ? window.ptTranslations.t('aria.atomicWeightLabel', 'Atomic Weight') : 'Atomic Weight';

    const ariaLabel = `${name}, ${symbolText} ${symbol}, ${atomicNumberText} ${atomicNumber}, ${atomicWeightText} ${atomicWeight}`;

    if (ariaLabel) {
      tile.setAttribute('aria-label', ariaLabel);
    }

    // Data attributes for filtering
    const attributes = {
      'data-z': element.atomicNumber,
      'data-group': element.group,
      'data-period': element.period,
      'data-type': element.type,
      'data-subtype': element.subtype
    };

    Object.entries(attributes).forEach(([attr, value]) => {
      if (value !== undefined && value !== null) {
        tile.setAttribute(attr, String(value));
      }
    });
  }

  addTileClasses(tile, element) {
    const slugify = (str) => String(str || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

    if (element.type) {
      tile.classList.add(`pt-type-${slugify(element.type)}`);
    }

    if (element.subtype) {
      const subtypeClass = `pt-subtype-${slugify(element.subtype)}`;
      tile.classList.add(subtypeClass);
      
      const responsiveTile = tile.querySelector('.pt-inv-area');
      if (responsiveTile) {
        responsiveTile.classList.add(`${subtypeClass}-inv`);
      }
    }

    if (element.state) {
      const stateClass = `pt-state-${slugify(element.state)}`;
      tile.classList.add(stateClass);
      
      const responsiveTile = tile.querySelector('.pt-inv-area');
      if (responsiveTile) {
        responsiveTile.classList.add(`${stateClass}-inv`);
      }
    }
  }

  createFallbackTile(element) {
    const tile = document.createElement('div');
    tile.className = 'pt-element-tile';
    tile.setAttribute('role', 'button');
    tile.setAttribute('tabindex', '0');
    this.setTileAttributes(tile, element);
    this.addTileClasses(tile, element);
    return tile;
  }

  createEmptyTile() {
    const tile = document.createElement('div');
    tile.className = 'pt-element-tile pt-element-empty';
    tile.style.background = 'var(--white-0)!important';
    return tile;
  }

  /**
   * Build period/group map for efficient lookups
   */
  buildElementMap(elements) {
    return elements.reduce((map, element) => {
      const period = Number(element.period) || 0;
      const group = element.group !== null && element.group !== undefined 
        ? Number(element.group) 
        : null;

      if (!map[period]) {
        map[period] = {};
      }

      if (group !== null) {
        map[period][group] = element;
      }

      return map;
    }, {});
  }

  /**
   * Render main periodic table grid (18 columns × 7 rows)
   */
  renderMainGrid(elementMap) {
    const grid = document.createElement('div');
    grid.className = 'pt-grid-md-18';

    // Add infographic placeholder
    this.addInfoGraphicPlaceholder(grid);

    // Get all elements for f-block lookup
    const elements = this.dataLoader.getElements();

    // Render 7 periods × 18 groups
    for (let period = 1; period <= 7; period++) {
      for (let group = 1; group <= 18; group++) {
        // Skip cells covered by infographic placeholder (periods 1-3, groups 3-12)
        const inPlaceholder = period >= 1 && period <= 3 && group >= 3 && group <= 12;
        if (inPlaceholder) continue;

        const element = elementMap[period]?.[group];
        const tile = element ? this.createElementTile(element) : this.createEmptyTile();

        tile.style.gridColumnStart = String(group);
        tile.style.gridRowStart = String(period);
        grid.appendChild(tile);

        // Insert f-block elements behind La (57) and Ac (89)
        // For period 6, group 3: insert Lanthanides (58-71) behind La (57)
        // For period 7, group 3: insert Actinides (90-103) behind Ac (89)
        if (period === 6 && group === 3) {
          // Insert Lanthanides (58-71) in the same grid position as La
          const lanthanides = elements.filter(el => 
            el.atomicNumber >= 58 && el.atomicNumber <= 71
          ).sort((a, b) => a.atomicNumber - b.atomicNumber);
          
          lanthanides.forEach(lanthanide => {
            const lanthanideTile = this.createElementTile(lanthanide);
            lanthanideTile.classList.add('pt-fblock-inline');
            lanthanideTile.style.gridColumnStart = String(group);
            lanthanideTile.style.gridRowStart = String(period);
            grid.appendChild(lanthanideTile);
          });
        }

        if (period === 7 && group === 3) {
          // Insert Actinides (90-103) in the same grid position as Ac
          const actinides = elements.filter(el => 
            el.atomicNumber >= 90 && el.atomicNumber <= 103
          ).sort((a, b) => a.atomicNumber - b.atomicNumber);
          
          actinides.forEach(actinide => {
            const actinideTile = this.createElementTile(actinide);
            actinideTile.classList.add('pt-fblock-inline');
            actinideTile.style.gridColumnStart = String(group);
            actinideTile.style.gridRowStart = String(period);
            grid.appendChild(actinideTile);
          });
        }
      }
    }

    return grid;
  }

  addInfoGraphicPlaceholder(grid) {
    const placeholder = document.createElement('div');
    placeholder.className = 'pt-infoCard kmd-d-none';
    
    const template = document.getElementById('pt-infographic-template');
    if (template) {
      placeholder.innerHTML = template.innerHTML;
    }
    
    grid.appendChild(placeholder);
  }

  /**
   * Render f-block (lanthanides and actinides)
   */
  renderFBlock(elements) {
    const wrapper = document.createElement('div');
    wrapper.className = 'pt-fblock-wrapper';

    // Get lanthanides and actinides
    const lanthanides = this.getFBlockElements(elements, 'lanthanide', 6, 58, 71);
    const actinides = this.getFBlockElements(elements, 'actinide', 7, 90, 103);

    // Normalize to configured max length
    const normalizedLanthanides = this.normalizeToLength(lanthanides, AppConfig.MAX_F_BLOCK_ELEMENTS);
    const normalizedActinides = this.normalizeToLength(actinides, AppConfig.MAX_F_BLOCK_ELEMENTS);

    // Create rows
    wrapper.appendChild(this.createFBlockRow(normalizedLanthanides, 'pt-lanthanides-row'));
    wrapper.appendChild(this.createFBlockRow(normalizedActinides, 'pt-actinides-row'));

    return wrapper;
  }

  getFBlockElements(elements, subtypeKeyword, period, minZ, maxZ) {
    // Try to find by subtype first
    let fElements = elements.filter(el => 
      el.subtype && el.subtype.toLowerCase().includes(subtypeKeyword)
    );

    // Fallback: filter by period and atomic number range
    if (fElements.length === 0) {
      fElements = elements.filter(el => 
        Number(el.period) === period && 
        Number(el.atomicNumber) >= minZ && 
        Number(el.atomicNumber) <= maxZ
      );
    }

    return fElements;
  }

  normalizeToLength(array, targetLength) {
    const result = [...array];
    while (result.length < targetLength) {
      result.push(null);
    }
    return result.slice(0, targetLength);
  }

  createFBlockRow(elements, className) {
    const row = document.createElement('div');
    row.className = `pt-fblock ${className}`;

    elements.forEach(element => {
      const tile = element ? this.createElementTile(element) : this.createEmptyTile();
      row.appendChild(tile);
    });

    return row;
  }

  /**
   * Main render method
   */
  render() {
    if (!this.container) {
      console.error('Table container not found');
      return;
    }

    const elements = this.dataLoader.getElements();
    const elementMap = this.buildElementMap(elements);

    // Clear container and render
    this.container.innerHTML = '';
    
    // Render main grid
    const mainGrid = this.renderMainGrid(elementMap);
    this.container.appendChild(mainGrid);

    // Render f-block
    const fBlock = this.renderFBlock(elements);
    this.container.appendChild(fBlock);

    EventBus.dispatch('pt:table-rendered');
  }
}

// ============================================================================
// FILTER MANAGER - Handles all filter logic and UI
// ============================================================================

class FilterManager {
  constructor(dataLoader) {
    this.dataLoader = dataLoader;
    this.state = {
      primary: null,
      secondary: []
    };

    this.elements = {
      primaryContainer: document.getElementById('pt-filters-primary'),
      secondaryContainer: document.getElementById('pt-filters-secondary'),
      secondaryInner: document.getElementById('pt-filters-secondary-container'),
      badge: document.getElementById('pt-custom-badge'),
      resetLink: document.querySelector('.pt-reset-all-filters')
    };

    this.init();
  }

  init() {
    this.attachPrimaryHandlers();
    this.attachResetHandler();
    this.clearSecondary();
    
    // Initialize aria-labels for primary chips after shadow DOM is ready
    setTimeout(() => {
      AccessibilityUtils.updateAllChipAriaLabels(this.elements.primaryContainer);
    }, 100);
  }

  attachPrimaryHandlers() {
    if (!this.elements.primaryContainer) return;

    // Listen to the core-chip's native 'selection' event instead of handling clicks/keyboard ourselves
    // The component already handles all interaction internally (mouse, keyboard, touch)
    // We handle both selection and deselection so the app state stays in sync with the component.
    this.elements.primaryContainer.addEventListener('selection', (e) => {
      const chip = e.target.closest('core-chip');
      if (!chip || !this.elements.primaryContainer.contains(chip)) return;

      const value = chip.getAttribute('data-attr') || chip.getAttribute('label');

      if (e.detail === true) {
        // Chip selected
        this.selectPrimaryChip(chip);
      } else {
        // Chip deselected: if it matches our current primary state, reset filters
        if (value && this.state.primary && String(value) === String(this.state.primary)) {
          this.reset();
        }
      }
    });
  }

  selectPrimaryChip(chip) {
    // Deselect all other chips
    this.elements.primaryContainer.querySelectorAll('core-chip')
      .forEach(c => {
        if (c !== chip) {
          c.removeAttribute('selected');
          c.selected = false; // Also set the property for web component
          // Update aria-label to remove 'selected' state
          AccessibilityUtils.updateChipAriaLabel(c);
        }
      });

    // The chip is already selected by the component's internal logic
    // Just ensure it has the selected attribute
    if (!chip.hasAttribute('selected')) {
      chip.setAttribute('selected', '');
    }
    chip.selected = true; // Ensure property is set

    // Update aria-label to include 'selected' state for screen readers
    AccessibilityUtils.updateChipAriaLabel(chip);

    const value = chip.getAttribute('data-attr') || chip.getAttribute('label');
    
    // Apply filters after state updates
    this.setPrimary(value);
  }

  attachResetHandler() {
    if (!this.elements.resetLink) return;

    this.elements.resetLink.addEventListener('click', (e) => {
      e.preventDefault();
      this.reset();
    });

    // Add keyboard support for reset link
    this.elements.resetLink.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.reset();
      }
    });
  }

  setPrimary(value) {
    this.state.primary = value;
    this.state.secondary = [];
    
    if (!value) {
      this.clearSecondary();
      this.updateActiveFilterBadgeDisplay();
      this.applyFilters();
      return;
    }

    this.populateSecondary(value);
    this.updateActiveFilterBadgeDisplay();
    this.applyFilters();
    EventBus.dispatch('pt:primary-changed', { value });
  }

  toggleSecondary(value) {
    const index = this.state.secondary.indexOf(value);
    
    if (index === -1) {
      this.state.secondary.push(value);
    } else {
      this.state.secondary.splice(index, 1);
    }

    this.updateActiveFilterBadgeDisplay();
    this.applyFilters();
    EventBus.dispatch('pt:secondary-changed', { values: [...this.state.secondary] });
  }

  clearSecondary() {
    this.state.secondary = [];
    
    if (this.elements.secondaryInner) {
      this.elements.secondaryInner.innerHTML = '';
    }

    DOMUtils.hide(this.elements.secondaryContainer);
    DOMUtils.hide(this.elements.resetLink);
    
    EventBus.dispatch('pt:secondary-cleared');
  }

  populateSecondary(primary) {
    const primaryKey = String(primary).toLowerCase();

    // Special case: metalloids have no secondary filters
    if (primaryKey === 'metalloids') {
      this.clearSecondary();
      return;
    }

    const options = this.getSecondaryOptions(primaryKey);

    if (!options || options.length === 0) {
      this.clearSecondary();
      return;
    }

    // Show secondary container
    DOMUtils.show(this.elements.secondaryContainer);
    DOMUtils.show(this.elements.resetLink);

    // Populate chips
    if (this.elements.secondaryInner) {
      this.elements.secondaryInner.innerHTML = '';
      options.forEach(opt => {
        const chip = this.createChip(opt.label, opt.value);
        this.elements.secondaryInner.appendChild(chip);
      });

      this.elements.secondaryInner.setAttribute("role", "list");

      // Update aria-labels for all chips after they're added to DOM
      // Give chips time to initialize their shadow DOM
      setTimeout(() => {
        AccessibilityUtils.updateAllChipAriaLabels(this.elements.secondaryInner);
      }, 100);

      // Attach a single delegated listener for secondary selection changes.
      // Avoid attaching multiple listeners if populateSecondary is called repeatedly.
      if (!this._secondarySelectionAttached) {
        this._secondarySelectionAttached = true;
        this.elements.secondaryInner.addEventListener('selection', (e) => {
          const chip = e.target.closest('core-chip');
          if (!chip) return;

          this.toggleSecondaryChip(chip, e.detail);
        });
      }

      // Move focus to secondary filters container for screen reader accessibility
      AccessibilityUtils.restoreFocus(this.elements.secondaryInner, 100);
    }
  }

  toggleSecondaryChip(chip, isSelected) {
    const value = chip.getAttribute('data-attr');
    if (value === undefined || value === null) return;

    // Update state according to the explicit selection flag from the component
    const idx = this.state.secondary.indexOf(value);
    if (isSelected === true) {
      if (idx === -1) this.state.secondary.push(value);
    } else if (isSelected === false) {
      if (idx !== -1) this.state.secondary.splice(idx, 1);
    } else {
      // Fallback: toggle if selection state not provided
      if (idx === -1) this.state.secondary.push(value); else this.state.secondary.splice(idx, 1);
    }

    // Update aria-label to reflect selection state for screen readers
    AccessibilityUtils.updateChipAriaLabel(chip);

    this.updateActiveFilterBadgeDisplay();
    this.applyFilters();
    EventBus.dispatch('pt:secondary-changed', { values: [...this.state.secondary] });
  }

  getSecondaryOptions(primaryKey) {
    const elements = this.dataLoader.getElements();

    switch (primaryKey) {
      case 'states':
        return this.getUniqueValues(elements, 'state');

      case 'groups':
        return this.getUniqueNumericValues(elements, 'group');

      case 'periods':
        return this.getUniqueNumericValues(elements, 'period');

      case 'metals':
      case 'nonmetals':
        return this.getMetalOptions(primaryKey, elements);

      default:
        return [];
    }
  }

  getUniqueValues(elements, key) {
    const uniqueSet = new Set(
      elements
        .map(el => el[key])
        .filter(val => val !== null && val !== undefined)
    );

    return Array.from(uniqueSet)
      .sort()
      .map(val => ({ value: val, label: val }));
  }

  getUniqueNumericValues(elements, key) {
    const uniqueSet = new Set(
      elements
        .map(el => el[key])
        .filter(val => val !== null && val !== undefined)
        .map(val => Number(val))
    );

    return Array.from(uniqueSet)
      .sort((a, b) => a - b)
      .map(val => ({ value: String(val), label: val }));
  }

  getMetalOptions(primaryKey, elements) {
    const typeMap = {
      'metals': 'Metal',
      'metalloids': 'Metalloid',
      'nonmetals': 'Nonmetal'
    };

    const targetType = typeMap[primaryKey];
    const filtered = elements.filter(el => 
      el.type && el.type.toLowerCase() === targetType.toLowerCase()
    );

    // Try subtypes first
    const subtypes = new Set(
      filtered
        .map(el => el.subtype)
        .filter(st => st !== null && st !== undefined)
    );

    if (subtypes.size > 0) {
      // Define custom ordering for subtypes
      const metalOrder = [
        'Alkali Metals',
        'Alkaline Earth Metals',
        'Transition Metals',
        'Post-Transition Metals',
        'Lanthanides',
        'Actinides'
      ];

      const nonmetalOrder = [
        'Nonmetals',
        'Halogens',
        'Noble Gases'
      ];

      // Choose the appropriate order based on primary filter
      const customOrder = primaryKey === 'metals' ? metalOrder : nonmetalOrder;

      // Convert Set to Array and sort by custom order
      const subtypeArray = Array.from(subtypes);
      const orderedSubtypes = subtypeArray.sort((a, b) => {
        const indexA = customOrder.indexOf(a);
        const indexB = customOrder.indexOf(b);
        
        // If both are in the custom order, sort by their position
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }
        
        // If only one is in the custom order, prioritize it
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        
        // If neither is in the custom order, sort alphabetically
        return a.localeCompare(b);
      });

      return orderedSubtypes.map(st => ({ value: st, label: st }));
    }

    // Fallback to groups
    const groups = new Set(
      filtered
        .map(el => el.group)
        .filter(g => g !== null && g !== undefined)
        .map(g => Number(g))
    );

    if (groups.size > 0) {
      return Array.from(groups)
        .sort((a, b) => a - b)
        .map(g => ({ value: String(g), label: `Group ${g}` }));
    }

    // Last resort: symbols
    const symbols = new Set(filtered.map(el => el.symbol).filter(Boolean));
    return Array.from(symbols)
      .sort()
      .map(sym => ({ value: `sym:${sym}`, label: sym }));
  }

  createChip(label, dataAttr) {
    const chip = document.createElement('core-chip');
    chip.setAttribute('variant', 'selectable');
    
    // Translate the label if it's an element property value
    const translatedLabel = this.translateChipLabel(label, dataAttr);
    chip.setAttribute('label', translatedLabel);
    
    // Don't set role, tabindex, or aria-pressed - the component handles these internally
    if (dataAttr !== undefined) {
      chip.setAttribute('data-attr', dataAttr);
    }
    return chip;
  }

  /**
   * Translate chip label for secondary filters
   * @param {string} label - The original label
   * @param {string} dataAttr - The data attribute value (used for identifying property type)
   * @returns {string} - Translated label or original if no translation available
   */
  translateChipLabel(label, dataAttr) {
    const translationManager = window.ptTranslations;
    if (!translationManager) return label;

    // Skip numeric labels (groups/periods) and symbol filters
    if (!isNaN(Number(label)) || String(dataAttr).startsWith('sym:')) {
      return label;
    }

    // Translate using the element property translation system
    // The translateElementProperty method handles all types (subtype, state, type) automatically
    const translated = translationManager.translateElementProperty(label);
    return translated;
  }

  updateActiveFilterBadgeDisplay() {
    const { primary, secondary } = this.state;
    const hasActive = !!primary || secondary.length > 0;

    if (this.elements.badge) {
      DOMUtils.toggleClass(this.elements.badge, 'pt-custom-badge-active', hasActive);
    }
  }

  applyFilters() {
    const elements = this.dataLoader.getElements();
    const filtered = this.filterElements(elements);

    // Close any open inline expansion cards when filters change
    EventBus.dispatch('pt:close-inline-expansion');

    // this.updateResults(filtered);
    this.updateTableVisibility(filtered);
  }

  filterElements(elements) {
    const { primary, secondary } = this.state;

    if (!primary) {
      return elements;
    }

    const primaryKey = String(primary).toLowerCase();
    let filtered = elements;

    // Apply primary filter
    if (['metals', 'metalloids', 'nonmetals'].includes(primaryKey)) {
      const typeMap = { 'metals': 'Metal', 'metalloids': 'Metalloid', 'nonmetals': 'Nonmetal' };
      const targetType = typeMap[primaryKey];
      filtered = filtered.filter(el => 
        el.type && el.type.toLowerCase() === targetType.toLowerCase()
      );
    } else if (primaryKey === 'states') {
      // When States primary filter is applied, show only elements that have pt-state-* class
      filtered = filtered.filter(el => {
        if (!el.atomicNumber) return false;
        const tile = document.querySelector(`.pt-element-tile[data-z="${el.atomicNumber}"]`);
        if (!tile) return false;
        
        // Check if the tile has any pt-state-* class
        return Array.from(tile.classList).some(cls => cls.startsWith('pt-state-'));
      });
    }

    // Apply secondary filters
    if (secondary.length > 0) {
      filtered = this.applySecondaryFilters(filtered, primaryKey, secondary);
    }

    return filtered;
  }

  applySecondaryFilters(elements, primaryKey, secondaryValues) {
    switch (primaryKey) {
      case 'states':
        return elements.filter(el => secondaryValues.includes(el.state));

      case 'groups':
        return elements.filter(el => 
          secondaryValues.includes(String(el.group))
        );

      case 'periods':
        return elements.filter(el => 
          secondaryValues.includes(String(el.period))
        );

      case 'metals':
      case 'nonmetals':
        return elements.filter(el => 
          secondaryValues.some(val => {
            if (val.startsWith('sym:')) {
              return el.symbol === val.split(':')[1];
            }
            return el.subtype === val || String(el.group) === val;
          })
        );

      default:
        return elements;
    }
  }

  updateTableVisibility(filteredElements) {
    const { primary, secondary } = this.state;
    const hasActiveFilter = !!primary || secondary.length > 0;

    if (!hasActiveFilter) {
      // No filters: show all elements
      VisibilityUtils.showAllElements();
      return;
    }

    // Use shared visibility utility
    VisibilityUtils.updateElementVisibility(filteredElements);
  }

  reset() {
    // Deselect all primary chips
    this.elements.primaryContainer?.querySelectorAll('core-chip')
      .forEach(chip => {
        chip.removeAttribute('selected');
        // Update aria-label to remove 'selected' state for screen readers
        AccessibilityUtils.updateChipAriaLabel(chip);
      });

    this.state.primary = null;
    this.state.secondary = [];

    this.clearSecondary();
    this.updateActiveFilterBadgeDisplay();
    this.applyFilters();
    
    EventBus.dispatch('pt:filters-reset');
  }

  getState() {
    return { ...this.state };
  }
}

// ============================================================================
// INFOGRAPHIC MANAGER - Handles the info card display
// ============================================================================

class InfographicManager {
  constructor(dataLoader, filterManager, translationManager) {
    this.dataLoader = dataLoader;
    this.filterManager = filterManager;
    this.translationManager = translationManager;
    this.elementCache = null; // Lazy cache for DOM elements

    this.init();
  }

  /**
   * Lazy getter for DOM elements - queries on first access or when cache is cleared
   */
  getElements() {
    if (!this.elementCache) {
      this.elementCache = {
        placeholder: document.querySelector('.pt-infoCard'),
        responsivePlaceholder: document.querySelector('.pt-responsive-info-content'),
        title: document.querySelector('#pt-infographic-title'),
        description: document.querySelector('#pt-infographic-description'),
        titleResponsive: document.querySelector('#pt-infographic-title-responsive'),
        descriptionResponsive: document.querySelector('#pt-infographic-description-responsive')
      };
    }
    return this.elementCache;
  }

  /**
   * Clear element cache (useful after table re-render)
   */
  clearCache() {
    this.elementCache = null;
  }

  /**
   * Translate infographic title using general_content translations
   */
  translateTitle(englishTitle) {
    if (!this.translationManager) {
      return englishTitle;
    }

    const translations = this.translationManager.data;
    
    // Check if translations and filters exist
    if (!translations || !translations.filters) {
      console.warn('Translation data not available, using English title:', englishTitle);
      return englishTitle;
    }
    
    // Map English titles to their translation keys in general_content.filters
    const titleKeyMap = {
      'Groups': 'primary.groups',
      'Periods': 'primary.periods',
      'Metalloids': 'primary.metalloids',
      'Alkali Metals': 'secondary.alkaliMetals',
      'Alkaline Earth Metals': 'secondary.alkalineEarthMetals',
      'Transition Metals': 'secondary.transitionMetals',
      'Post-Transition Metals': 'secondary.postTransitionMetals',
      'Lanthanides': 'secondary.lanthanides',
      'Actinides': 'secondary.actinides',
      'Nonmetals': 'secondary.nonmetals',
      'Halogens': 'secondary.halogens',
      'Noble Gases': 'secondary.nobleGases'
    };

    const translationKey = titleKeyMap[englishTitle];
    if (translationKey) {
      const [section, key] = translationKey.split('.');
      if (translations.filters[section] && translations.filters[section][key]) {
        return translations.filters[section][key];
      }
    }

    return englishTitle;
  }

  init() {
    EventBus.on('pt:primary-changed', () => this.update());
    EventBus.on('pt:secondary-changed', () => this.update());
    EventBus.on('pt:secondary-cleared', () => this.update());
    EventBus.on('pt:filters-reset', () => this.hide());
    EventBus.on('pt:infographic-loaded', () => this.update());
    EventBus.on('pt:table-rendered', () => {
      this.clearCache(); // Clear cache when table re-renders
      this.update(); // Update infographic after table is ready
    });
  }

  update() {
    const { primary, secondary } = this.filterManager.getState();
    const primaryKey = primary ? String(primary).toLowerCase() : '';

    // Special case: Groups or Periods primary filter
    if (primaryKey === 'groups' || primaryKey === 'periods' || primaryKey === 'metalloids') {
      this.showPrimaryDescription(primaryKey);
      return;
    }

    // Default: show when exactly one secondary filter is active
    if (!secondary || secondary.length !== 1) {
      this.hide();
      return;
    }

    this.showSecondaryDescription(secondary[0]);
  }

  showPrimaryDescription(primaryKey) {
    // Map primary filter keys to their display names in the infographic data
    const keyMap = {
      'groups': 'Groups',
      'periods': 'Periods',
      'metalloids': 'Metalloids'
    };
    
    const lookupKey = keyMap[primaryKey] || primaryKey;
    const infographicData = this.dataLoader.getInfographic();
    
    const description = infographicData[lookupKey];

    if (!description) {
      console.warn('No description found for key:', lookupKey);
      this.hide();
      return;
    }

    this.show(lookupKey, description);
  }

  showSecondaryDescription(secondaryValue) {
    const key = String(secondaryValue);

    // Skip symbol-style filters
    if (key.startsWith('sym:')) {
      this.hide();
      return;
    }

    // Skip numeric values (groups/periods)
    if (!isNaN(Number(key))) {
      this.hide();
      return;
    }

    // Look up description
    const infographicData = this.dataLoader.getInfographic();
    
    let description = infographicData[key];

    // Case-insensitive fallback
    if (!description) {
      const lowerKey = key.toLowerCase();
      const foundKey = Object.keys(infographicData).find(k => 
        k.toLowerCase() === lowerKey
      );
      if (foundKey) {
        description = infographicData[foundKey];
      }
    }

    if (!description) {
      console.warn('No description found for secondary key:', key);
      this.hide();
      return;
    }

    this.show(key, description);
  }

  show(title, description) {
    const elements = this.getElements();
    
    DOMUtils.show(elements.placeholder);
    DOMUtils.show(elements.responsivePlaceholder);

    // Translate the title before displaying
    const translatedTitle = this.translateTitle(title);

    if (elements.title) {
      elements.title.textContent = translatedTitle;
    }
    if (elements.description) {
      // Allow HTML tags including scripts from trusted element-details descriptions
      elements.description.innerHTML = description;
    }
    if (elements.titleResponsive) {
      elements.titleResponsive.textContent = translatedTitle;
    }
    if (elements.descriptionResponsive) {
      // Allow HTML tags including scripts from trusted element-details descriptions
      elements.descriptionResponsive.innerHTML = description;
    }
  }

  hide() {
    const elements = this.getElements();
    DOMUtils.hide(elements.placeholder);
    DOMUtils.hide(elements.responsivePlaceholder);
  }
}

// ============================================================================
// MOBILE FILTER TOGGLE - Handles mobile filter visibility
// ============================================================================

class MobileFilterToggle {
  constructor() {
    this.toggle = document.getElementById('pt-mobile-filter-toggle');
    this.filters = document.getElementById('pt-filters');
    
    if (this.toggle && this.filters) {
      this.init();
    }
  }

  init() {
    this.toggle.addEventListener('click', (e) => {
      e.preventDefault();
      
      if (DOMUtils.hasClass(this.filters, 'kmd-d-none')) {
        DOMUtils.show(this.filters);
      } else {
        DOMUtils.hide(this.filters);
      }
    });
  }
}

// ============================================================================
// CORE TABLE MANAGER - Handles tabular view with expandable details
// ============================================================================

class CoreTableManager {
  constructor(dataLoader) {
    this.dataLoader = dataLoader;
    this.ptTable = document.getElementById('pt-core-table');
    this.currentOpen = null;
    this.currentPostareaNode = null;
    this.detailsData = {};
  }

  async init() {
    if (!this.ptTable) {
      console.warn('pt-core-table element not found');
      return;
    }

    try {
      // Load elements data
      const elements = this.dataLoader.getElements();
      
      // Transform elements for display with translated properties
      const displayElements = this.prepareElementsForDisplay(elements);
      
      // Set table data
      if ('data' in this.ptTable) {
        this.ptTable.data = displayElements;
      } else if ('rows' in this.ptTable) {
        this.ptTable.rows = displayElements;
      } else {
        // Fallback: set attribute with JSON string
        try { 
          this.ptTable.setAttribute('data', JSON.stringify(displayElements)); 
        } catch (error) {
          console.error('Failed to set table data:', error);
        }
      }

      // Set initial aria-rowcount
      this.updateTableAriaRowCount(elements.length);

      // Load element details
      this.detailsData = this.dataLoader.getDetails();

      // Create toggle buttons for each row (pass original elements for metadata)
      this.createToggleButtons(elements);

      console.debug('Core Table initialized successfully');
    } catch (error) {
      console.error('Error initializing core table:', error);
    }
  }

  /**
   * Prepare elements for display by translating type/subtype/state values
   * Creates a copy of elements with translated display properties
   * @param {Array} elements - Original element data
   * @returns {Array} - Elements with translated display values
   */
  prepareElementsForDisplay(elements) {
    const translationManager = window.ptTranslations;
    if (!translationManager) return elements;

    return elements.map(element => ({
      ...element,
      // Add translated display properties while keeping original values for data attributes
      typeDisplay: translationManager.translateElementProperty(element.type, 'type'),
      subtypeDisplay: translationManager.translateElementProperty(element.subtype, 'subtype'),
      stateDisplay: translationManager.translateElementProperty(element.state, 'state'),
      // Keep original values for CSS classes and filtering
      type: element.type,
      subtype: element.subtype,
      state: element.state
    }));
  }

  createToggleButtons(elements) {
    elements.forEach(el => {
      const atomicNumber = el.atomicNumber;
      const span = document.createElement('span');
      span.setAttribute('slot', `cell-${atomicNumber}-toggle`);

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'icon-button pt-chevron-container kmd-p-2';
      AccessibilityUtils.setAriaExpanded(btn, false);
      AccessibilityUtils.setAriaControls(btn, `pt-element-details-${atomicNumber}`);
      btn.setAttribute('data-pt-toggle', String(atomicNumber));
      
      // Set descriptive aria-label for screen readers
      const elementName = el.name || '';
      const elementSymbol = el.symbol || '';
      AccessibilityUtils.updateButtonAriaLabel(btn, false, elementName, elementSymbol, atomicNumber);

      // Create CSS-only chevron
      const chevron = document.createElement('span');
      chevron.className = 'pt-chevron pt-chevron-down';
      chevron.setAttribute('aria-hidden', 'true');
      btn.appendChild(chevron);

      // Attach click handler
      btn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        const atomic = Number(btn.getAttribute('data-pt-toggle'));
        const isOpen = this.currentOpen === atomic;
        
        // Update aria state
        AccessibilityUtils.setAriaExpanded(btn, !isOpen);
        
        // Update aria-label based on new state
        AccessibilityUtils.updateButtonAriaLabel(btn, !isOpen, elementName, elementSymbol, atomicNumber);
        
        if (isOpen) {
          this.closeCurrent();
        } else {
          this.openDetails(atomic, btn, el);
        }
      });

      span.appendChild(btn);
      this.ptTable.appendChild(span);
    });
  }

  closeCurrent() {
    if (!this.currentOpen) return;

    // Clear postareas and remove DOM node
    try { 
      this.ptTable.postareas = []; 
    } catch (error) {
      console.error('Failed to clear postareas:', error);
      this.ptTable.postareas = [];
    }

    if (this.currentPostareaNode && this.currentPostareaNode.parentNode === this.ptTable) {
      this.ptTable.removeChild(this.currentPostareaNode);
    }

    // Reset chevron state
    const btn = this.ptTable.querySelector(`[data-pt-toggle='${this.currentOpen}']`);
    if (btn) {
      btn.classList.remove('pt-chevron-container--open');
      AccessibilityUtils.setAriaExpanded(btn, false);
      
      // Get element details for aria-label update
      const elements = this.dataLoader.getElements();
      const element = elements.find(el => String(el.atomicNumber) === String(this.currentOpen));
      if (element) {
        AccessibilityUtils.updateButtonAriaLabel(btn, false, element.name || '', element.symbol || '', this.currentOpen);
      }
    }

    this.currentOpen = null;
    this.currentPostareaNode = null;
  }

  openDetails(atomicNumber, btn, elementMeta) {
    // Close existing if different
    if (this.currentOpen === atomicNumber) {
      this.closeCurrent();
      return;
    }

    this.closeCurrent();

    // Create postarea node
    const postareaNode = document.createElement('div');
    postareaNode.setAttribute('slot', `postarea-${atomicNumber}-1`);
    postareaNode.className = 'pt-core-postarea kmd-pt-4';

    // Build details card
    const cardNode = this.buildDetailsCard(atomicNumber, elementMeta, 'table');
    if (cardNode instanceof Node) {
      postareaNode.appendChild(cardNode);
    } else {
      postareaNode.innerHTML = String(cardNode);
    }

    // Attach to table DOM
    this.ptTable.appendChild(postareaNode);

    // Inform table to render postarea
    try { 
      this.ptTable.postareas = [{ uid: Number(atomicNumber), index: 1 }]; 
    } catch (error) {
      console.error('Failed to set postareas:', error);
      this.ptTable.postareas = [{ uid: Number(atomicNumber), index: 1 }];
    }

    // Update state
    this.currentOpen = atomicNumber;
    this.currentPostareaNode = postareaNode;
    
    if (btn) btn.classList.add('pt-chevron-container--open');
  }

  buildDetailsCard(atomicNumber, elementMeta, type) {
    const detail = this.detailsData[String(atomicNumber)];

    let template = null;
    
    switch (type) {
      case 'inline':
        template = document.getElementById('pt-element-details-responsive-card');
        break;
      case 'modal':
        template = document.getElementById('pt-element-details-modal-card');
        break;
      default:
        template = document.getElementById('pt-element-details-card');
        break;
    }

    if (!template || !detail) {
      return document.createElement('div');
    }

    // Get the translated element name from the elements data
    const elements = this.dataLoader.getElements();
    const element = elements.find(el => String(el.atomicNumber) === String(atomicNumber));
    
    // Create a merged detail object with translated name
    const translatedDetail = {
      ...detail,
      tile: {
        ...detail.tile,
        name: element?.name || detail.tile?.name // Use translated name if available
      }
    };

    // Clone template
    const clone = template.content.firstElementChild.cloneNode(true);

    // Apply styling classes based on element type
    const slugify = (str) => String(str || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

    if (elementMeta) {
      const subtype = elementMeta.subtype || elementMeta.type;
      if (subtype) {
        const cls = `pt-subtype-${slugify(subtype)}`;
        const detailsElement = clone.querySelector('.pt-element-details-element');
        if (detailsElement) detailsElement.classList.add(cls);

        const inv = clone.querySelector('.pt-inv-area');
        if (inv) inv.classList.add(`${cls}-inv`);
      }
    }

    // Populate basic tile fields with translated detail
    this.populateTileFields(clone, translatedDetail, atomicNumber);
    
    // Populate description with translated detail
    this.populateDescription(clone, translatedDetail);
    
    // Populate product links
    this.populateProductLinks(clone, translatedDetail);
    
    // Populate properties
    this.populateProperties(clone, translatedDetail);

    return clone;
  }

  populateTileFields(clone, detail, atomicNumber) {
    const symbolEl = clone.querySelector('.pt-symbol');
    const atomicNumEl = clone.querySelector('.pt-atomic-number');
    const nameEl = clone.querySelector('.pt-name');
    const weightEl = clone.querySelector('.pt-atomic-weight');

    if (symbolEl) symbolEl.textContent = detail.tile?.symbol || '';
    if (atomicNumEl) atomicNumEl.textContent = String(detail.tile?.atomicNumber || atomicNumber);
    if (nameEl) nameEl.textContent = detail.tile?.name || '';
    if (weightEl) weightEl.textContent = detail.tile?.atomicWeight || '';
  }

  populateDescription(clone, detail) {
    const descContainer = clone.querySelector('.pt-element-description');
    if (!descContainer) return;

    descContainer.innerHTML = '';
    const descriptions = detail.description || [];
    const elementName = detail.tile?.name || '';
    
    descriptions.forEach(text => {
      const p = document.createElement('p');
      p.className = 'kmd-text-sml kmd-mb-4 kmd-mt-md-0';
      // Allow HTML content in descriptions
      p.innerHTML = text;
      descContainer.appendChild(p);
    });

    // Add "Read more" toggle if multiple paragraphs
    const paragraphs = descContainer.querySelectorAll('p');
    if (paragraphs.length > 1) {
      // Hide all but first paragraph
      for (let i = 1; i < paragraphs.length; i++) {
        paragraphs[i].style.display = 'none';
      }

      const toggle = document.createElement('div');
      toggle.className = 'pt-read-more kmd-text-link';
      toggle.setAttribute('expanded', 'false');
      toggle.setAttribute('role', 'button');
      toggle.setAttribute('tabindex', '0');
      toggle.setAttribute('aria-label', (window.ptTranslations ? window.ptTranslations.t('buttons.readMore', 'Read More') : 'Read More') + ', ' + elementName);
      toggle.textContent = (window.ptTranslations ? window.ptTranslations.t('buttons.readMore', 'Read More') : 'Read More');
      
      // Click and keyboard handler
      const toggleHandler = () => {
        const expanded = toggle.getAttribute('expanded') === 'true';
        if (expanded) {
          for (let i = 1; i < paragraphs.length; i++) {
            paragraphs[i].style.display = 'none';
          }
          toggle.textContent = (window.ptTranslations ? window.ptTranslations.t('buttons.readMore', 'Read More') : 'Read More');
          toggle.setAttribute('expanded', 'false');
          toggle.setAttribute('aria-label', (window.ptTranslations ? window.ptTranslations.t('buttons.readMore', 'Read More') : 'Read More') + ', ' + elementName)
        } else {
          for (let i = 1; i < paragraphs.length; i++) {
            paragraphs[i].style.display = '';
          }
          toggle.textContent = (window.ptTranslations ? window.ptTranslations.t('buttons.showLess', 'Show Less') : 'Show Less');
          toggle.setAttribute('aria-label', (window.ptTranslations ? window.ptTranslations.t('buttons.showLess', 'Show Less') : 'Show Less') + ', ' + elementName)
          toggle.setAttribute('expanded', 'true');
        }
      };

      toggle.addEventListener('click', toggleHandler);
      toggle.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleHandler();
        }
      });

      descContainer.appendChild(toggle);
    }
  }

  /**
   * Create an arrow icon element for links
   * @returns {HTMLElement} Arrow icon span element
   */
  createLinkArrowIcon() {
    const arrowSpan = document.createElement('span');
    const arrowImg = document.createElement('img');
    arrowImg.src = 'static/images/arrow-right.svg';
    arrowImg.alt = '';
    arrowImg.setAttribute('aria-hidden', 'true');
    arrowImg.className = 'pt-link-arrow kmd-ml-2';
    arrowImg.style.height = '0.65rem';
    arrowSpan.appendChild(arrowImg);
    return arrowSpan;
  }

  /**
   * Create a clickable product link card
   * @param {Object} config - Configuration object
   * @param {string} config.category - Card header/category text
   * @param {string} config.linkText - Link text content
   * @param {string} config.linkHref - Link href attribute
   * @param {string} config.ariaLabel - Accessible label for the card
   * @param {string} [config.linkTarget='_blank'] - Link target attribute
   * @param {Function} [config.onAnchorClick] - Optional click handler for anchor
   * @param {Object} [config.anchorAttributes] - Optional additional anchor attributes
   * @param {string} [config.cardClassName='pt-product-link kmd-p-4 kmd-mb-6'] - Card class name
   * @returns {HTMLElement} Clickable card element
   */
  createProductLinkCard(config) {
    const {
      category,
      linkText,
      linkHref,
      ariaLabel,
      linkTarget = '_blank',
      onAnchorClick,
      anchorAttributes = {},
      cardClassName = 'pt-product-link kmd-p-4 kmd-mb-6'
    } = config;

    // Create card container
    const card = document.createElement('div');
    card.className = cardClassName;
    card.setAttribute('role', 'link');
    card.setAttribute('tabindex', '0');
    card.style.cursor = 'pointer';
    card.setAttribute('aria-label', ariaLabel);

    // Create content wrapper
    const content = document.createElement('div');
    content.className = 'pt-product-content';

    // Create header
    const header = document.createElement('h4');
    header.className = 'kmd-mt-0 kmd-mb-2';
    header.textContent = category;
    content.appendChild(header);

    // Create anchor
    const anchor = document.createElement('a');
    anchor.href = linkHref;
    anchor.className = 'kmd-link kmd-text-sml';
    anchor.textContent = linkText;
    anchor.setAttribute('tabindex', '-1'); // Remove from tab order since card is focusable
    anchor.setAttribute('aria-hidden', 'true'); // Hide from screen readers since card has aria-label
    
    // Set target for external links
    if (linkTarget) {
      anchor.target = linkTarget;
      if (linkTarget === '_blank') {
        anchor.rel = 'noopener noreferrer';
      }
    }

    // Apply additional anchor attributes
    Object.entries(anchorAttributes).forEach(([key, value]) => {
      anchor.setAttribute(key, value);
    });

    // Add custom click handler if provided
    if (onAnchorClick && typeof onAnchorClick === 'function') {
      anchor.addEventListener('click', onAnchorClick);
    }

    // Add arrow icon
    anchor.appendChild(this.createLinkArrowIcon());
    content.appendChild(anchor);
    card.appendChild(content);

    // Make the entire card clickable
    this.attachCardClickHandlers(card, anchor);

    return card;
  }

  /**
   * Attach click and keyboard handlers to make a card trigger its inner anchor
   * @param {HTMLElement} card - The card element
   * @param {HTMLElement} anchor - The anchor element inside the card
   */
  attachCardClickHandlers(card, anchor) {
    // Click handler
    const cardClickHandler = (e) => {
      // Don't trigger if user is clicking directly on the anchor
      if (e.target.tagName === 'A' || e.target.closest('a')) {
        return;
      }
      e.preventDefault();
      anchor.click();
    };

    card.addEventListener('click', cardClickHandler);
    
    // Keyboard handler (Enter/Space)
    card.addEventListener('keydown', (e) => {
      if (AccessibilityUtils.isActivationKey(e)) {
        e.preventDefault();
        anchor.click();
      }
    });
  }

  populateProductLinks(clone, detail) {
    const linksContainer = clone.querySelector('.pt-element-details-links');
    if (!linksContainer) return;

    linksContainer.innerHTML = '';
    const links = detail.productLinks || [];
    
    if(links.length !== 0) {
      linksContainer.classList.add('kmd-pt-2');
    }

    // Create a card for each product link
    links.forEach(link => {
      const card = this.createProductLinkCard({
        category: link.category || '',
        linkText: link.text || link.href || 'More',
        linkHref: LocaleUtils.buildLocalizedUrl(link.href) || '#',
        ariaLabel: `Opens in a new tab, ${link.category || 'Product link'}, ${link.text || link.href || 'More'}`,
        linkTarget: '_blank'
      });
      
      linksContainer.appendChild(card);
    });

    // Add dynamic chemical search link only if there are existing product links
    if (links.length > 0) {
      this.addChemicalSearchLink(linksContainer, detail);
    }
  }

  /**
   * Add dynamic chemical search link that performs structure search
   * @param {HTMLElement} container - The links container element
   * @param {Object} detail - The element detail object containing tile information
   */
  addChemicalSearchLink(container, detail) {
    const elementName = detail.tile?.name;
    const elementSymbol = detail.tile?.symbol;
    
    if (!elementName || !elementSymbol) return;

    const card = this.createProductLinkCard({
      category: (window.ptTranslations ? window.ptTranslations.t('search.searchCategory', 'Search') : 'Search'),
      linkText: `${window.ptTranslations ? window.ptTranslations.t('search.chemicalsContaining', 'Chemicals containing') : 'Chemicals containing'} ${elementName}`,
      linkHref: '#',
      ariaLabel: `${window.ptTranslations ? window.ptTranslations.t('aria.opensInNewTabPrefix', 'Opens in a new tab') : 'Opens in a new tab'}, ${window.ptTranslations ? window.ptTranslations.t('search.searchCategory', 'Search') : 'Search'}, ${window.ptTranslations ? window.ptTranslations.t('search.chemicalsContaining', 'Chemicals containing') : 'Chemicals containing'} ${elementName}`,
      linkTarget: null, // No target for hash links
      cardClassName: 'pt-product-link kmd-p-4', // Different className (no margin)
      onAnchorClick: (e) => {
        e.preventDefault();
        this.handleChemicalSearch(elementSymbol, elementName);
      },
      anchorAttributes: {
        'data-element-symbol': elementSymbol,
        'data-element-name': elementName
      }
    });
    
    container.appendChild(card);
  }

  /**
   * Handle chemical structure search and redirect to results
   * @param {string} elementSymbol - The chemical element symbol (e.g., "H", "He")
   * @param {string} elementName - The chemical element name (e.g., "Hydrogen")
   */
  async handleChemicalSearch(elementSymbol, elementName) {
    try {
      // Validate inputs
      if (!elementSymbol || !/^[A-Z][a-z]?$/.test(elementSymbol)) {
        console.error('Invalid element symbol:', elementSymbol);
        return;
      }

      if (!elementName || typeof elementName !== 'string') {
        console.error('Invalid element name:', elementName);
        return;
      }

      // Get locale information for URL construction
      const locale = LocaleUtils.getLocaleCookie();
      const { country, language } = LocaleUtils.parseLocale(locale);

      // Step 1: Perform structure search
      const structureSearchUrl = '/search/chemical/structureSearch/?isExactSearch=false';
      // Sanitize element symbol to prevent XML injection
      const sanitizedSymbol = SecurityUtils.escapeHTML(elementSymbol);
      const cmlBody = `<cml><MDocument><MChemicalStruct><molecule molID="m1"><atomArray><atom id="a1" elementType="${sanitizedSymbol}"/></atomArray><bondArray/></molecule></MChemicalStruct></MDocument></cml>`;

      const structureResponse = await fetch(structureSearchUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/xml;charset=UTF-8',
        },
        body: cmlBody
      });

      if (!structureResponse.ok) {
        throw new Error(`Structure search failed: ${structureResponse.status}`);
      }

      const structureData = await structureResponse.json();
      
      // Check if casNumbers exists and is not empty
      if (!structureData.casnumbers || !Array.isArray(structureData.casnumbers) || structureData.casnumbers.length === 0) {
        alert(`No products found containing ${elementName}`);
        return;
      }

      // Step 2: Perform search results call with CAS numbers
      const casNumberList = structureData.casnumbers.join(',');
      const searchResultsUrl = `/${country}/${language}/search/chemical/searchresults`;
      
      const formData = {
        'casNumberList': casNumberList,
        'countryCode': country,
        'language': language
      };

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = searchResultsUrl;
      form.target = '_blank';
      form.rel = 'noopener noreferrer'; // Security: prevent window.opener access

      for (const key in formData) {
        if (Object.prototype.hasOwnProperty.call(formData, key)) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = formData[key];
          form.appendChild(input);
        }
      }

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);

    } catch (error) {
      console.error('Chemical search error:', error);
      alert(`Error searching for products containing ${elementName}. Please try again later.`);
    }
  }

  populateProperties(clone, detail) {
    const propsContainer = clone.querySelector('.pt-elements-properties');
    if (!propsContainer) return;

    propsContainer.innerHTML = '';
    const props = detail.properties || {};
    
    const propDiv = document.createElement('div');
    propDiv.className = 'kmd-pl-lg-6 kmd-text-left';

    const propEntries = Object.entries(props);
    propEntries.forEach(([key, value], idx) => {
      const isLast = idx === propEntries.length - 1;

      const entry = document.createElement('div');
      // Skip adding bottom padding class for the last entry
      if (!isLast) {
      entry.className = 'kmd-pb-2';
      }

      const keySpan = document.createElement('span');
      keySpan.className = 'kmd-text-weight-600 kmd-text-sml kmd-text-gray-100';
      
      // Translate the property key using the translation system
      const translatedKey = this.translatePropertyKey(key);
      keySpan.textContent = translatedKey + ':';

      const valueSpan = document.createElement('span');
      valueSpan.className = 'kmd-text-sml kmd-ml-2';
      
      // Preserve safe HTML tags (sup, sub, i, b, em, strong) for formatting
      // while preventing dangerous tags like script, iframe, etc.
      const stringValue = String(value);
      if (this.containsSafeHTMLOnly(stringValue)) {
        // Safe to use innerHTML for formatted content like electronic configurations
        if(key == 'Phase at STP')
          SecurityUtils.safeSetInnerHTML(valueSpan, this.translatePropertyKey(stringValue));
        else
          SecurityUtils.safeSetInnerHTML(valueSpan, stringValue);
      } else {
        // Use textContent for plain text or potentially unsafe content
        valueSpan.textContent = stringValue;
      }

      entry.appendChild(keySpan);
      entry.appendChild(valueSpan);
      propDiv.appendChild(entry);
    });
    propsContainer.appendChild(propDiv);
  }

  /**
   * Translate property key using the translation system
   * @param {string} key - The property key in English
   * @returns {string} - Translated property key or original if no translation available
   */
  translatePropertyKey(key) {
    const translationManager = window.ptTranslations;
    if (!translationManager) return key;

    // Access the properties section directly from the translation data
    const propertiesSection = translationManager.data?.properties;
    if (!propertiesSection) return key;

    // Look up the key directly (handles keys with spaces)
    const translated = propertiesSection[key];
    
    // If no translation found, return original
    if (!translated) {
      return key;
    }
    
    return translated;
  }

  /**
   * Check if string contains only safe HTML tags (sup, sub, i, b, em, strong)
   * @param {string} str - String to check
   * @returns {boolean} True if contains only safe tags or no tags
   */
  containsSafeHTMLOnly(str) {
    // If no HTML tags present, return true (plain text is safe)
    if (!/<[^>]+>/.test(str)) {
      return true;
    }

    // Remove all safe tags and check if any tags remain
    const withoutSafeTags = str.replace(/<\/?(?:sup|sub|i|b|em|strong)>/gi, '');
    
    // If tags still remain, they're unsafe
    return !/<[^>]+>/.test(withoutSafeTags);
  }

  /**
   * Update table rows with filtered elements
   * Called by CoreSearchManager to filter table based on search
   */
  updateRows(elements) {
    if (!this.ptTable) {
      console.warn('pt-core-table element not found');
      return;
    }

    try {
      // Close any open details when filtering
      this.closeCurrent();

      // Update table data with filtered elements
      if ('data' in this.ptTable) {
        this.ptTable.data = elements;
      } else if ('rows' in this.ptTable) {
        this.ptTable.rows = elements;
      } else {
        // Fallback: set attribute with JSON string
        try { 
          this.ptTable.setAttribute('data', JSON.stringify(elements)); 
        } catch (error) {
          console.error('Failed to update table data:', error);
        }
      }

      // Update aria-rowcount on the table container to reflect filtered results
      this.updateTableAriaRowCount(elements.length);

      // Note: Toggle buttons are already created during init() and are tied to atomic numbers,
      // so they don't need to be recreated when filtering. The core-table component will
      // automatically show/hide the appropriate toggles based on the filtered data.
    } catch (error) {
      console.error('Error updating table rows:', error);
    }
  }

  /**
   * Update aria-rowcount attribute on the table container
   * @param {number} rowCount - Number of rows currently displayed
   */
  updateTableAriaRowCount(rowCount) {
    const tableContainer = document.getElementById('pt-tabular-data-table');
    if (tableContainer) {
      tableContainer.setAttribute('aria-rowcount', String(rowCount));
    }
  }
}

// ============================================================================
// ELEMENT MODAL MANAGER - Handles modal display for element details
// Mobile: Shows inline expansion below clicked element
// Desktop: Shows modal dialog
// ============================================================================

class ElementModalManager {
  constructor(dataLoader, coreTableManager) {
    this.dataLoader = dataLoader;
    this.coreTableManager = coreTableManager;
    this.modal = document.getElementById('pt-element-description-modal');
    this.gridContainer = document.getElementById('pt-data-container');
    
    // Track current state
    this.currentOpenAtomicNumber = null;
    this.currentInlineElement = null;
    
    // Track the element that triggered the modal for focus restoration
    this.lastFocusedElement = null;
    
    // Focus trap handler reference (for adding/removing listener)
    this.focusTrapHandler = null;
    
    // Media query for desktop breakpoint
    this.desktopMediaQuery = window.matchMedia(`(min-width: ${AppConfig.MOBILE_BREAKPOINT}px)`);
    
    if (this.modal && this.gridContainer) {
      this.init();
    }
  }

  init() {
    // Wait for table to be rendered, then attach click handlers to grid elements
    EventBus.on('pt:table-rendered', () => {
      this.attachGridHandlers();
    });

    // Listen for breakpoint changes
    this.desktopMediaQuery.addEventListener('change', (e) => {
      this.handleBreakpointChange(e.matches);
    });

    // Listen for close inline expansion events (triggered by filters/search)
    EventBus.on('pt:close-inline-expansion', () => {
      this.closeInlineExpansion();
    });
    
    // Listen for modal close event to restore focus and remove focus trap
    if (this.modal) {
      this.modal.addEventListener('modalClosed', () => {
        this.removeFocusTrap();
        this.restoreFocus();
      });
    }
  }

  attachGridHandlers() {
    // Get all .pt-element-tile elements within pt-data-container (grid only, not table)
    if (!this.gridContainer) return;

    const tiles = this.gridContainer.querySelectorAll('.pt-element-tile:not(.pt-element-empty)');
    
    tiles.forEach(tile => {
      // Get atomic number from data attribute
      const atomicNumber = tile.getAttribute('data-z');
      if (!atomicNumber) return;

      // Click handler
      tile.addEventListener('click', (e) => {
        this.handleElementClick(atomicNumber, tile);
      });

      // Keyboard handler (Enter or Space)
      tile.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handleElementClick(atomicNumber, tile);
        }
      });
    });
  }

  handleElementClick(atomicNumber, tile) {
    // Don't open details for dimmed elements
    // Commented until clarification on dimmed elements
    // if (tile.classList.contains('pt-element-dimmed')) {
    //   return;
    // }
    
    // Store the element that triggered the modal for focus restoration
    this.lastFocusedElement = tile;
    
    const isDesktop = this.desktopMediaQuery.matches;
    
    // Toggle behavior: if clicking the same element, close it
    if (this.currentOpenAtomicNumber === atomicNumber) {
      if (isDesktop) {
        this.closeModal();
      } else {
        this.closeInlineExpansion();
      }
      return;
    }

    // Open element details
    if (isDesktop) {
      this.closeInlineExpansion(); // Close any mobile expansion first
      this.openModal(atomicNumber);
    } else {
      this.closeModal(); // Close any desktop modal first
      this.openInlineExpansion(atomicNumber, tile);
    }
  }

  handleBreakpointChange(isDesktop) {
    // When transitioning between mobile/desktop, clean up the opposite display
    if (this.currentOpenAtomicNumber) {
      if (isDesktop) {
        // Switching to desktop: close inline, show modal
        this.closeInlineExpansion();
        this.openModal(this.currentOpenAtomicNumber);
      } else {
        // Switching to mobile: close modal, show inline
        this.closeModal();
        const tile = this.gridContainer.querySelector(`[data-z="${this.currentOpenAtomicNumber}"]`);
        if (tile) {
          this.openInlineExpansion(this.currentOpenAtomicNumber, tile);
        }
      }
    }
  }

  openModal(atomicNumber) {
    if (!this.modal) return;

    const elements = this.dataLoader.getElements();
    const element = elements.find(el => String(el.atomicNumber) === String(atomicNumber));
    
    if (!element) {
      console.warn(`Element with atomic number ${atomicNumber} not found`);
      return;
    }

    // Update modal header with element name
    this.modal.setAttribute('header', element.name || 'Element');

    // Build the details card content using the same method as CoreTableManager
    const detailsCard = this.coreTableManager.buildDetailsCard(atomicNumber, element, 'modal');
    
    // Update modal content
    this.updateModalContent(detailsCard);

    // Use modalOpened event with double requestAnimationFrame to ensure shadow DOM is fully rendered
    // and all CSS transitions/animations have started
    const openedHandler = () => {
      // Double RAF ensures the browser has completed all rendering and transitions
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.setModalFocus();
        });
      });
      this.modal.removeEventListener('modalOpened', openedHandler);
    };
    this.modal.addEventListener('modalOpened', openedHandler);

    // Open the modal using the core-modal API
    this.modal.isopen = true;
    
    // Track current state
    this.currentOpenAtomicNumber = atomicNumber;
  }

  /**
   * Set focus to the modal for keyboard accessibility.
   * Delegates to AccessibilityUtils for consistent focus management.
   */
  setModalFocus() {
    if (!this.modal) return;
    
    // Set aria-label on the close button in shadow DOM
    AccessibilityUtils.setModalCloseButtonAriaLabel(this.modal);
    
    // Set up focus trap to keep focus within modal
    this.setupFocusTrap();
    
    const modalHeader = this.modal.getAttribute('header') || 'Element Details';
    AccessibilityUtils.setModalFocus(this.modal, modalHeader);
  }

  /**
   * Get all focusable elements within the modal (including shadow DOM)
   * @returns {Promise<Array<HTMLElement>>} Array of focusable elements
   */
  async getFocusableElements() {
    if (!this.modal) return [];
    
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');
    
    const focusableElements = [];
    
    // Get focusable elements from shadow DOM (close button) - wait for it asynchronously
    try {
      const shadowRoot = await AccessibilityUtils.waitForShadowDOMElement(this.modal, '*', 500);
      if (shadowRoot) {
        const shadowFocusable = this.modal.shadowRoot.querySelectorAll(focusableSelectors);
        focusableElements.push(...Array.from(shadowFocusable));
      }
    } catch (error) {
      console.debug('Shadow DOM not available for modal focus trap');
    }
    
    // Get focusable elements from light DOM (modal content)
    const lightFocusable = this.modal.querySelectorAll(focusableSelectors);
    focusableElements.push(...Array.from(lightFocusable));
    
    // Filter out elements that are not visible
    return focusableElements.filter(el => {
      return el.offsetWidth > 0 && 
             el.offsetHeight > 0 && 
             !el.hasAttribute('hidden');
    });
  }

  /**
   * Set up focus trap to keep focus within modal
   */
  async setupFocusTrap() {
    if (!this.modal) return;
    
    // Remove existing focus trap if any
    this.removeFocusTrap();
    
    // Create focus trap handler
    this.focusTrapHandler = async (e) => {
      // Only trap Tab key
      if (e.key !== 'Tab') return;
      
      const focusableElements = await this.getFocusableElements();
      
      if (focusableElements.length === 0) {
        // If no focusable elements, prevent tabbing
        e.preventDefault();
        return;
      }
      
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      
      // Shift + Tab: if on first element, move to last
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } 
      // Tab: if on last element, move to first
      else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };
    
    // Add focus trap listener to the document
    document.addEventListener('keydown', this.focusTrapHandler);
  }

  /**
   * Remove focus trap when modal closes
   */
  removeFocusTrap() {
    if (this.focusTrapHandler) {
      document.removeEventListener('keydown', this.focusTrapHandler);
      this.focusTrapHandler = null;
    }
  }

  closeModal() {
    if (!this.modal) return;
    
    // Remove focus trap before closing modal
    this.removeFocusTrap();
    
    this.modal.isopen = false;
    this.currentOpenAtomicNumber = null;
    // Note: Focus restoration happens in modalClosed event handler
  }

  /**
   * Restore focus to the element that triggered the modal
   * Called automatically when modal closes via modalClosed event
   */
  restoreFocus() {
    if (this.lastFocusedElement && document.contains(this.lastFocusedElement)) {
      AccessibilityUtils.restoreFocus(this.lastFocusedElement, 50);
    }
  }

  openInlineExpansion(atomicNumber, clickedTile) {
    if (!this.gridContainer) return;

    // Close any existing inline expansion first to ensure only one is open at a time
    this.closeInlineExpansion();

    const elements = this.dataLoader.getElements();
    const element = elements.find(el => String(el.atomicNumber) === String(atomicNumber));
    
    if (!element) {
      console.warn(`Element with atomic number ${atomicNumber} not found`);
      return;
    }

    // Build the details card content
    const detailsCard = this.coreTableManager.buildDetailsCard(atomicNumber, element, 'inline');
    
    // Create wrapper for inline expansion
    const wrapper = document.createElement('div');
    wrapper.className = 'pt-inline-expansion kmd-p-4';
    wrapper.setAttribute('data-expansion-for', atomicNumber);

    wrapper.appendChild(detailsCard);
    
    // Find the appropriate position to insert (after the clicked tile's row)
    const insertPosition = this.findInsertPosition(clickedTile);
    
    if (insertPosition) {
      insertPosition.insertAdjacentElement('afterend', wrapper);
    } else {
      // Fallback: append to grid container
      this.gridContainer.appendChild(wrapper);
    }
    
    // Track current state
    this.currentOpenAtomicNumber = atomicNumber;
    this.currentInlineElement = wrapper;
    
    // Scroll to the expanded content smoothly
    setTimeout(() => {
      wrapper.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  }

  closeInlineExpansion() {
    if (this.currentInlineElement) {
      this.currentInlineElement.remove();
      this.currentInlineElement = null;
    }
    this.currentOpenAtomicNumber = null;
  }

  findInsertPosition(clickedTile) {
    // For mobile view, tiles are displayed linearly in the grid
    // We want to insert after the clicked tile
    return clickedTile;
  }

  updateModalContent(contentNode) {
    if (!this.modal) return;

    // Clear existing content (but preserve footer slot)
    const existingContent = Array.from(this.modal.children).filter(
      child => !child.hasAttribute('slot') || child.getAttribute('slot') !== 'footer'
    );
    
    existingContent.forEach(child => child.remove());

    // Add new content
    this.modal.appendChild(contentNode);
  }
}

// ============================================================================
// KEYBOARD NAVIGATION MANAGER - Handles arrow key navigation for desktop
// ============================================================================

class KeyboardNavigationManager {
  constructor() {
    this.gridContainer = document.getElementById('pt-data-container');
    this.tableContainer = document.getElementById('pt-table-container');
    this.desktopMediaQuery = window.matchMedia(`(min-width: ${AppConfig.MOBILE_BREAKPOINT}px)`);
    this.isEnabled = false;
    this.hasActiveFilters = false;
    
    // Grid structure (period, group) mapping for main table
    // Periods 1-7, Groups 1-18
    this.mainGridMap = new Map();
    
    // F-block structure (row, position) mapping
    // Row 1 (Lanthanides): positions 1-15
    // Row 2 (Actinides): positions 1-15
    this.fBlockMap = new Map();
    
    // Bound event handler for proper removal
    this.keyDownHandler = (e) => {
      if (this.isEnabled) {
        this.handleKeyDown(e);
      }
    };
    
    if (this.gridContainer) {
      this.init();
    }
  }

  init() {
    // Enable/disable based on viewport
    this.updateEnabledState();
    this.desktopMediaQuery.addEventListener('change', (e) => {
      this.updateEnabledState();
    });

    // Attach keyboard listener to grid container
    this.gridContainer.addEventListener('keydown', this.keyDownHandler);

    // Re-build maps when table is rendered or filters change
    EventBus.on('pt:table-rendered', () => {
      this.rebuildMaps();
      this.updateTabIndexes();
    });
    
    EventBus.on('pt:filters-reset', () => {
      this.hasActiveFilters = false;
      this.rebuildMaps();
      this.updateTabIndexes();
    });
    
    EventBus.on('pt:primary-changed', () => {
      this.hasActiveFilters = true;
      this.rebuildMaps();
      this.updateTabIndexes();
    });
    
    EventBus.on('pt:secondary-changed', () => {
      this.hasActiveFilters = true;
      this.rebuildMaps();
      this.updateTabIndexes();
    });
  }

  rebuildMaps() {
    // Use setTimeout to ensure DOM has updated
    setTimeout(() => {
      this.mainGridMap = this.buildMainGridMap();
      this.fBlockMap = this.buildFBlockMap();
    }, 100);
  }

  updateEnabledState() {
    this.isEnabled = this.desktopMediaQuery.matches;
  }

  /**
   * Update tabindex values for roving tabindex pattern
   * When filters are active, all active (non-dimmed) elements get tabindex="0"
   * Dimmed elements get tabindex="-1"
   * This allows Tab to reach all active elements
   */
  updateTabIndexes() {
    if (!this.isEnabled) return;

    // Get all element tiles
    const allTiles = this.gridContainer.querySelectorAll('.pt-element-tile:not(.pt-element-empty)');
    
    if (this.hasActiveFilters) {
      allTiles.forEach(tile => {
        if (this.isElementDimmed(tile)) {
          // Dimmed elements are not in tab order
          tile.setAttribute('tabindex', '-1');
        } else {
          // Active (non-dimmed) elements are tabbable
          tile.setAttribute('tabindex', '0');
        }
      });
      
      // Remove tabindex from table container when filters are active
      if (this.tableContainer) {
        this.tableContainer.setAttribute('tabindex', '-1');
      }
    } else {
      // No filters: all elements are equally accessible via tab
      allTiles.forEach(tile => {
        tile.setAttribute('tabindex', '0');
      });
      
      // Restore table container tabindex
      if (this.tableContainer) {
        this.tableContainer.setAttribute('tabindex', '0');
      }
    }
  }

  /**
   * Find the first active (non-dimmed) element in the periodic table
   * Searches in reading order: main grid top to bottom, left to right, then f-block
   */
  findFirstActiveElement() {
    // Search main grid first (periods 1-7, groups 1-18)
    for (let period = 1; period <= 7; period++) {
      for (let group = 1; group <= 18; group++) {
        const key = `${period}-${group}`;
        const element = this.mainGridMap.get(key);
        
        if (element && this.isElementFocusable(element) && !this.isElementDimmed(element)) {
          return element;
        }
      }
    }
    
    // Search f-block if no active element found in main grid
    // Lanthanides first (row 1)
    for (let position = 1; position <= 15; position++) {
      const key = `1-${position}`;
      const element = this.fBlockMap.get(key);
      
      if (element && this.isElementFocusable(element) && !this.isElementDimmed(element)) {
        return element;
      }
    }
    
    // Actinides (row 2)
    for (let position = 1; position <= 15; position++) {
      const key = `2-${position}`;
      const element = this.fBlockMap.get(key);
      
      if (element && this.isElementFocusable(element) && !this.isElementDimmed(element)) {
        return element;
      }
    }
    
    return null;
  }

  /**
   * Check if an element is dimmed (filtered out)
   */
  isElementDimmed(element) {
    return element && element.classList.contains('pt-element-dimmed');
  }

  /**
   * Build a map of all navigable elements in the main grid
   * Returns: Map<string, element>
   */
  buildMainGridMap() {
    const map = new Map();
    
    if (!this.gridContainer) return map;
    
    const tiles = this.gridContainer.querySelectorAll('.pt-grid-md-18 > .pt-element-tile:not(.pt-element-empty):not(.pt-fblock-inline)');
    
    tiles.forEach(tile => {
      const period = tile.style.gridRowStart;
      const group = tile.style.gridColumnStart;
      if (period && group) {
        const key = `${period}-${group}`;
        map.set(key, tile);
      }
    });
    
    return map;
  }

  /**
   * Build a map of all navigable elements in the f-block
   * Returns: Map<string, element>
   * Row 1 = Lanthanides, Row 2 = Actinides
   */
  buildFBlockMap() {
    const map = new Map();
    
    if (!this.gridContainer) return map;
    
    // Lanthanides (row 1)
    const lanthanideRow = this.gridContainer.querySelector('.pt-lanthanides-row');
    if (lanthanideRow) {
      const lanthanides = lanthanideRow.querySelectorAll('.pt-element-tile:not(.pt-element-empty)');
      lanthanides.forEach((tile, index) => {
        map.set(`1-${index + 1}`, tile);
      });
    }
    
    // Actinides (row 2)
    const actinideRow = this.gridContainer.querySelector('.pt-actinides-row');
    if (actinideRow) {
      const actinides = actinideRow.querySelectorAll('.pt-element-tile:not(.pt-element-empty)');
      actinides.forEach((tile, index) => {
        map.set(`2-${index + 1}`, tile);
      });
    }
    
    return map;
  }

  /**
   * Handle arrow key navigation
   */
  handleKeyDown(e) {
    const target = e.target;
    
    // Only handle arrow keys
    if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      return;
    }

    // Only navigate if focus is on an element tile
    if (!target.classList.contains('pt-element-tile')) {
      return;
    }

    // Allow screen readers to handle navigation if modifier keys are pressed
    // Screen readers like Narrator, JAWS, NVDA use Ctrl, Alt, or Shift with arrow keys
    if (e.ctrlKey || e.altKey || e.shiftKey || e.metaKey) {
      return; // Let screen reader handle it
    }

    // Prevent default browser scrolling for plain arrow keys
    e.preventDefault();
    e.stopPropagation();

    const isInFBlock = this.isElementInFBlock(target);
    
    if (isInFBlock) {
      this.navigateFBlock(target, e.key);
    } else {
      this.navigateMainGrid(target, e.key);
    }
  }

  /**
   * Check if element is in f-block
   */
  isElementInFBlock(element) {
    return element.closest('.pt-fblock-wrapper') !== null;
  }

  /**
   * Navigate within the main grid
   */
  navigateMainGrid(currentTile, key) {
    const currentPeriod = parseInt(currentTile.style.gridRowStart);
    const currentGroup = parseInt(currentTile.style.gridColumnStart);

    // Validate current position
    if (isNaN(currentPeriod) || isNaN(currentGroup)) {
      console.warn('Invalid current position:', currentPeriod, currentGroup);
      return;
    }

    let targetPeriod = currentPeriod;
    let targetGroup = currentGroup;

    switch (key) {
      case 'ArrowUp':
        targetPeriod = currentPeriod - 1;
        break;
      case 'ArrowDown':
        targetPeriod = currentPeriod + 1;
        break;
      case 'ArrowLeft':
        targetGroup = currentGroup - 1;
        break;
      case 'ArrowRight':
        targetGroup = currentGroup + 1;
        break;
    }

    // Special case: Moving right from La (period 6, group 3) goes to first Lanthanide
    if (key === 'ArrowRight' && currentPeriod === 6 && currentGroup === 3) {
      this.jumpToFBlock(1, 1); // Jump to first lanthanide (Ce)
      return;
    }

    // Special case: Moving right from Ac (period 7, group 3) goes to first Actinide
    if (key === 'ArrowRight' && currentPeriod === 7 && currentGroup === 3) {
      this.jumpToFBlock(2, 1); // Jump to first actinide (Th)
      return;
    }

    // Special case: Moving down from period 7 group 3 goes to f-block
    if (key === 'ArrowDown' && currentPeriod === 7 && currentGroup === 3) {
      this.jumpToFBlock(1, 1); // Jump to first lanthanide
      return;
    }

    // Boundary checks
    if (targetPeriod < 1 || targetPeriod > 7) return;
    if (targetGroup < 1 || targetGroup > 18) return;

    // Try to find target element
    let targetKey = `${targetPeriod}-${targetGroup}`;
    let targetElement = this.mainGridMap.get(targetKey);

    // If target is in the infographic placeholder area, skip over it
    if (targetPeriod >= 1 && targetPeriod <= 3 && targetGroup >= 3 && targetGroup <= 12) {
      if (key === 'ArrowLeft') {
        // Jump to group 2
        targetGroup = 2;
        targetKey = `${targetPeriod}-${targetGroup}`;
        targetElement = this.mainGridMap.get(targetKey);
      } else if (key === 'ArrowRight') {
        // Jump to group 13
        targetGroup = 13;
        targetKey = `${targetPeriod}-${targetGroup}`;
        targetElement = this.mainGridMap.get(targetKey);
      } else if (key === 'ArrowUp' || key === 'ArrowDown') {
        // For vertical navigation through placeholder, try to maintain group
        // but if not possible, find nearest available
        targetElement = this.findNearestElement(targetPeriod, targetGroup);
      }
    }

    // If target element doesn't exist, search for next available element in the direction of movement
    if (!targetElement || !this.isElementFocusable(targetElement)) {
      targetElement = this.findNextElementInDirection(currentPeriod, currentGroup, key);
    }

    // Navigate if target exists and is visible/focusable
    if (targetElement && this.isElementFocusable(targetElement)) {
      this.focusElement(targetElement);
    }
  }

  /**
   * Find the next available element in the direction of arrow key movement
   */
  findNextElementInDirection(currentPeriod, currentGroup, key) {
    let searchPeriod = currentPeriod;
    let searchGroup = currentGroup;

    // Define search parameters based on direction
    switch (key) {
      case 'ArrowRight':
        // Search right across the same period, wrapping to next period if needed
        for (let g = currentGroup + 1; g <= 18; g++) {
          const element = this.mainGridMap.get(`${searchPeriod}-${g}`);
          if (element && this.isElementFocusable(element)) {
            return element;
          }
        }
        break;

      case 'ArrowLeft':
        // Search left across the same period, wrapping to previous period if needed
        for (let g = currentGroup - 1; g >= 1; g--) {
          const element = this.mainGridMap.get(`${searchPeriod}-${g}`);
          if (element && this.isElementFocusable(element)) {
            return element;
          }
        }
        break;

      case 'ArrowDown':
        // Search down in the same group first, then try adjacent groups
        for (let p = currentPeriod + 1; p <= 7; p++) {
          const element = this.mainGridMap.get(`${p}-${searchGroup}`);
          if (element && this.isElementFocusable(element)) {
            return element;
          }
        }
        // If not found in same group, try nearest element in next period
        return this.findNearestElement(currentPeriod + 1, currentGroup);

      case 'ArrowUp':
        // Search up in the same group first, then try adjacent groups
        for (let p = currentPeriod - 1; p >= 1; p--) {
          const element = this.mainGridMap.get(`${p}-${searchGroup}`);
          if (element && this.isElementFocusable(element)) {
            return element;
          }
        }
        // If not found in same group, try nearest element in previous period
        return this.findNearestElement(currentPeriod - 1, currentGroup);
    }

    return null;
  }

  /**
   * Navigate within the f-block
   */
  navigateFBlock(currentTile, key) {
    // Determine which row (1=Lanthanides, 2=Actinides)
    const isLanthanide = currentTile.closest('.pt-lanthanides-row') !== null;
    const currentRow = isLanthanide ? 1 : 2;
    
    // Find position in the row
    const row = currentTile.closest('.pt-fblock');
    if (!row) {
      console.warn('Could not find f-block row');
      return;
    }
    
    const tiles = Array.from(row.querySelectorAll('.pt-element-tile:not(.pt-element-empty)'));
    const currentPosition = tiles.indexOf(currentTile) + 1;

    if (currentPosition === 0) {
      console.warn('Could not find current tile in f-block');
      return;
    }

    let targetRow = currentRow;
    let targetPosition = currentPosition;

    switch (key) {
      case 'ArrowUp':
        if (currentRow === 2) {
          // From actinides to lanthanides
          targetRow = 1;
        } else {
          // From lanthanides, go to main grid period 6, group 3 (La)
          this.jumpToMainGrid(6, 3);
          return;
        }
        break;
      case 'ArrowDown':
        if (currentRow === 1) {
          // From lanthanides to actinides
          targetRow = 2;
        } else {
          // From actinides, go to main grid period 7, group 3 (Ac)
          this.jumpToMainGrid(7, 3);
          return;
        }
        break;
      case 'ArrowLeft':
        // If at first position, go back to La (period 6) or Ac (period 7)
        if (currentPosition === 1) {
          const period = currentRow === 1 ? 6 : 7; // La for lanthanides, Ac for actinides
          this.jumpToMainGrid(period, 3);
          return;
        }
        targetPosition = currentPosition - 1;
        break;
      case 'ArrowRight':
        // If at last position, go to Hf (period 6, group 4) or Rf (period 7, group 4)
        const maxPosition = tiles.length;
        if (currentPosition === maxPosition) {
          const period = currentRow === 1 ? 6 : 7; // Hf for lanthanides, Rf for actinides
          this.jumpToMainGrid(period, 4);
          return;
        }
        targetPosition = currentPosition + 1;
        break;
    }

    // Boundary checks for position
    if (targetPosition < 1 || targetPosition > 15) return;

    // Try to find target element
    const targetKey = `${targetRow}-${targetPosition}`;
    const targetElement = this.fBlockMap.get(targetKey);

    // Navigate if target exists and is visible/focusable
    if (targetElement && this.isElementFocusable(targetElement)) {
      this.focusElement(targetElement);
    }
  }

  /**
   * Check if an element is focusable (not hidden, can be dimmed)
   */
  isElementFocusable(element) {
    if (!element) return false;
    
    // Note: We DO allow dimmed elements to be focused - they're just filtered, not disabled
    
    // Check if element is visible
    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    
    // Check if element or any parent has kmd-d-none class
    if (element.classList.contains('kmd-d-none')) return false;
    if (element.closest('.kmd-d-none')) return false;
    
    return true;
  }

  /**
   * Jump from main grid to f-block
   */
  jumpToFBlock(row, position) {
    const targetKey = `${row}-${position}`;
    const targetElement = this.fBlockMap.get(targetKey);
    
    if (targetElement && this.isElementFocusable(targetElement)) {
      this.focusElement(targetElement);
    }
  }

  /**
   * Jump from f-block to main grid
   */
  jumpToMainGrid(period, group) {
    const targetKey = `${period}-${group}`;
    const targetElement = this.mainGridMap.get(targetKey);
    
    if (targetElement && this.isElementFocusable(targetElement)) {
      this.focusElement(targetElement);
    }
  }

  /**
   * Find nearest available element when target is in placeholder
   */
  findNearestElement(period, group) {
    // Search nearby groups in the same period
    for (let offset = 1; offset <= 18; offset++) {
      // Try right first
      const rightKey = `${period}-${group + offset}`;
      const rightElement = this.mainGridMap.get(rightKey);
      if (rightElement && this.isElementFocusable(rightElement)) {
        return rightElement;
      }
      
      // Try left
      const leftKey = `${period}-${group - offset}`;
      const leftElement = this.mainGridMap.get(leftKey);
      if (leftElement && this.isElementFocusable(leftElement)) {
        return leftElement;
      }
    }
    
    return null;
  }

  /**
   * Focus on an element and announce to screen readers
   */
  focusElement(element) {
    if (!element || !this.isElementFocusable(element)) {
      console.warn('Cannot focus element:', element);
      return;
    }
    
    // Ensure element is still in the DOM
    if (!document.contains(element)) {
      console.warn('Element not in DOM, rebuilding maps');
      this.rebuildMaps();
      return;
    }
    
    element.focus();
    
    // Get element details for announcement
    const atomicNumber = element.getAttribute('data-z');
    const symbolEl = element.querySelector('.pt-symbol, .pt-symbol-lg, .pt-symbol-md');
    const nameEl = element.querySelector('.pt-name, .pt-name-md');
    
    const symbol = symbolEl ? symbolEl.textContent : '';
    const name = nameEl ? nameEl.textContent : '';
    
    // Announce to screen readers
    if (name && symbol && atomicNumber) {
      if(window.ptTranslations) {
        const translatedSymbol = window.ptTranslations.t('aria.symbolLabel', 'Symbol');
        const translatedAtomicNumber = window.ptTranslations.t('aria.atomicNumberLabel', 'Atomic Number');
        const translatedMessage = `${name}, ${translatedSymbol} ${symbol}, ${translatedAtomicNumber} ${atomicNumber}`;
        AccessibilityUtils.announceToScreenReader(translatedMessage);
      }else{
        AccessibilityUtils.announceToScreenReader(`${name}, Symbol ${symbol}, Atomic Number ${atomicNumber}`);
      }
    }
  }
}

// ============================================================================
// BACK TO TOP BUTTON - Handles scroll-to-top functionality
// ============================================================================

class BackToTopButton {
  constructor() {
    this.btn = document.getElementById('pt-back-to-top');
    this.showAfter = AppConfig.SCROLL_THRESHOLD; // px scrolled before button appears
    this.visibleClass = 'pt-back-to-top--visible';
    
    if (this.btn) {
      this.init();
    }
  }

  init() {
    // Attach event listeners
    window.addEventListener('scroll', () => this.onScroll(), { passive: true });
    this.btn.addEventListener('click', () => this.scrollToTop());
    
    // Use AccessibilityUtils for keyboard support
    AccessibilityUtils.addKeyboardActivation(this.btn, () => this.scrollToTop());

    // Set initial state
    this.onScroll();
  }

  onScroll() {
    const scrolled = window.pageYOffset || 
                     document.documentElement.scrollTop || 
                     document.body.scrollTop || 
                     0;
    
    if (scrolled > this.showAfter) {
      this.btn.classList.add(this.visibleClass);
    } else {
      this.btn.classList.remove(this.visibleClass);
    }
  }

  scrollToTop() {
    try {
      if (this.supportsSmoothScroll()) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        window.scrollTo(0, 0);
      }
    } catch (e) {
      window.scrollTo(0, 0);
    }
    
    // Shift focus to the top of the page for accessibility
    AccessibilityUtils.shiftFocusToTop();
    
    // Announce to screen readers
    AccessibilityUtils.announceScrollToTop();
  }

  supportsSmoothScroll() {
    return 'scrollBehavior' in document.documentElement.style;
  }
}

// ============================================================================
// TABLE SEARCH MANAGER - Handles pt-table-search (mobile/tablet search)
// ============================================================================

class TableSearchManager {
  constructor(dataLoader, filterManager) {
    this.dataLoader = dataLoader;
    this.filterManager = filterManager;
    this.searchInput = document.getElementById('pt-table-search');
    this.searchQuery = '';
    
    if (this.searchInput) {
      this.init();
    }
  }

  init() {
    // Listen to search query changes
    this.searchInput.addEventListener('searchQueryChanged', (e) => {
      this.searchQuery = e.detail.trim();
      this.handleSearch();
      // Set aria-label on clear button when search has content
      AccessibilityUtils.setSearchClearButtonAriaLabel(this.searchInput);
    });

    // Listen to search cleared
    this.searchInput.addEventListener('searchCleared', () => {
      this.searchQuery = '';
      this.handleSearchClear();
    });
  }

  /**
   * Handle search - reset filters and apply search
   */
  handleSearch() {
    // Close any open inline expansion cards when search changes
    EventBus.dispatch('pt:close-inline-expansion');

    // Reset filters first
    this.filterManager.reset();

    if (!this.searchQuery) {
      VisibilityUtils.showAllElements();
      this.hideNoResultsMessage();
      return;
    }

    const elements = this.dataLoader.getElements();
    const matches = SearchUtils.searchElements(elements, this.searchQuery);

    if (matches.length === 0) {
      this.showNoResultsMessage();
      VisibilityUtils.disableAllElements();
    } else {
      this.hideNoResultsMessage();
      VisibilityUtils.updateElementVisibility(matches);
    }
  }

  /**
   * Handle search clear - show all elements and reset filters
   */
  handleSearchClear() {
    // Close any open inline expansion cards when search is cleared
    EventBus.dispatch('pt:close-inline-expansion');

    this.searchQuery = '';
    this.filterManager.reset();
    VisibilityUtils.showAllElements();
    this.hideNoResultsMessage();
  }

  /**
   * Show "no results" message
   */
  showNoResultsMessage() {
    let message = document.getElementById('pt-no-results-message');
    
    if (!message) {
      message = document.createElement('div');
      message.id = 'pt-no-results-message';
      message.className = 'kmd-text-center kmd-p-8 kmd-text-gray-60';
  message.textContent = window.ptTranslations ? window.ptTranslations.t('table.noResults', 'No elements match your search.') : 'No elements match your search.';
      
      const container = document.getElementById('pt-data-container');
      if (container) {
        container.appendChild(message);
      }
    } else {
      DOMUtils.show(message);
    }
  }

  /**
   * Hide "no results" message
   */
  hideNoResultsMessage() {
    const message = document.getElementById('pt-no-results-message');
    if (message) {
      DOMUtils.hide(message);
    }
  }
}

// ============================================================================
// CORE SEARCH MANAGER - Handles pt-core-search (desktop table search)
// ============================================================================

class CoreSearchManager {
  constructor(dataLoader, coreTableManager) {
    this.dataLoader = dataLoader;
    this.coreTableManager = coreTableManager;
    this.searchInput = document.getElementById('pt-core-search');
    this.searchQuery = '';
    this.allRows = [];
    
    if (this.searchInput) {
      this.init();
    }
  }

  init() {
    // Listen to search query changes
    this.searchInput.addEventListener('searchQueryChanged', (e) => {
      this.searchQuery = e.detail.trim();
      this.handleSearch();
      // Set aria-label on clear button when search has content
      AccessibilityUtils.setSearchClearButtonAriaLabel(this.searchInput);
    });

    // Listen to search cleared
    this.searchInput.addEventListener('searchCleared', () => {
      this.searchQuery = '';
      this.handleSearchClear();
    });

    // Store all rows initially
    EventBus.on('pt:data-loaded', () => {
      this.allRows = this.dataLoader.getElements();
    });
  }

  /**
   * Handle search - filter table rows
   */
  handleSearch() {
    if (!this.searchQuery) {
      this.showAllRows();
      return;
    }

    const elements = this.dataLoader.getElements();
    const matches = SearchUtils.searchElements(elements, this.searchQuery);

    this.updateTableRows(matches);
  }

  /**
   * Handle search clear - show all rows
   */
  handleSearchClear() {
    this.searchQuery = '';
    this.showAllRows();
  }

  /**
   * Update core-table rows based on search results
   */
  updateTableRows(matchedElements) {
    if (this.coreTableManager && this.coreTableManager.updateRows) {
      this.coreTableManager.updateRows(matchedElements);
    }
  }

  /**
   * Show all rows in the table
   */
  showAllRows() {
    if (this.coreTableManager && this.coreTableManager.updateRows) {
      this.coreTableManager.updateRows(this.dataLoader.getElements());
    }
  }
}

// ============================================================================
// SHARE LINKS MANAGER - Handles social media share links with keyboard accessibility
// ============================================================================

class ShareLinksManager {
  constructor() {
    this.facebookLink = document.querySelector('.pt-share-facebook');
    this.emailLink = document.querySelector('.pt-share-email');
    this.linkedInLink = document.querySelector('.pt-share-link[aria-label="LinkedIn"]');
    
    this.init();
  }

  init() {
    // Get current page URL without query parameters
    this.currentPageUrl = window.location.origin + window.location.pathname;
    
    // Update all share links with dynamic URL
    this.updateShareLinks();
    
    if (this.facebookLink) {
      this.attachShareHandler(this.facebookLink, this.openFacebookShare.bind(this));
    }
  }

  /**
   * Update share link URLs dynamically based on current page
   */
  updateShareLinks() {
    const pageUrl = encodeURIComponent(this.currentPageUrl);
    const pageTitle = 'Fisher%20Scientific%20Interactive%20Periodic%20Table';
    const pageMessage = 'Check%20out%20the%20Fisher%20Scientific%20Interactive%20Periodic%20Table';
    
    // Update Email link
    if (this.emailLink) {
      this.emailLink.href = `mailto:?subject=${pageTitle}&body=${pageMessage}%20at%20${pageUrl}`;
    }
    
    // Update Facebook data-share-url attribute
    if (this.facebookLink) {
      this.facebookLink.setAttribute('data-share-url', this.currentPageUrl);
    }
    
    // Update LinkedIn link
    if (this.linkedInLink) {
      this.linkedInLink.href = `http://www.linkedin.com/shareArticle?mini=true&url=${pageUrl}&title=${pageTitle}&summary=&source=fishersci.com`;
    }
  }

  /**
   * Attach click and keyboard handlers to a share link
   * @param {HTMLElement} element - The share link element
   * @param {Function} handler - The share handler function
   */
  attachShareHandler(element, handler) {
    if (!element) return;

    // Handle click events
    element.addEventListener('click', (e) => {
      e.preventDefault();
      handler(element);
    });

    // Handle keyboard events (Enter and Space)
    element.addEventListener('keydown', (e) => {
      if (AccessibilityUtils.isActivationKey(e)) {
        e.preventDefault();
        handler(element);
      }
    });
  }

  /**
   * Open Facebook share dialog
   * @param {HTMLElement} element - The Facebook share link element
   */
  openFacebookShare(element) {
    const quote = element.getAttribute('data-share-quote') || '';
    const url = element.getAttribute('data-share-url') || '';
    
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?quote=${encodeURIComponent(quote)}&u=${encodeURIComponent(url)}`;
    
    window.open(
      facebookUrl,
      'facebook-share-dialog',
      'width=626,height=436,resizable=yes,scrollbars=yes'
    );
  }
}

// ============================================================================
// MAIN APPLICATION - Orchestrates all components
// ============================================================================

class PeriodicTableApp {
  constructor() {
    this.translationManager = null;
    this.dataLoader = null;
    this.tableRenderer = null;
    this.filterManager = null;
    this.infographicManager = null;
    this.mobileToggle = null;
    this.coreTableManager = null;
    this.backToTopButton = null;
    this.tableSearchManager = null;
    this.coreSearchManager = null;
    this.elementModalManager = null;
    this.shareLinksManager = null;
    this.keyboardNavigationManager = null;
  }

  async init() {
    try {
      // Handle bfcache reload
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) {
          window.location.reload();
        }
      });

      // Load translations first so static text updates early
      this.translationManager = new TranslationManager();
      window.ptTranslations = this.translationManager;
      try {
        // Attempt to load locale-specific translation file; fallback handled inside TranslationManager
        await this.translationManager.load();
      } catch (txErr) {
  console.warn('Translation loading failed; continuing with defaults');
      }

      // Apply translations to DOM (safe no-op if selectors not present)
      try {
        this.translationManager.applyTranslations();
        // Dispatch event after translations are applied
        EventBus.dispatch('pt:translations-loaded');
        window.dispatchEvent(new CustomEvent('translationsLoaded'));
      } catch (applyErr) {
        console.warn('Applying translations failed');
      }

      // Populate document title and meta description from translations (fallback to existing values)
      try {
        const t = (key, fallback = '') => (this.translationManager && typeof this.translationManager.t === 'function') ? this.translationManager.t(key, fallback) : (fallback || '');
        const title = t('header.title', document.title || 'Interactive Periodic Table of Elements');
        if (title) document.title = title;

        // Simple HTML -> plain text stripper (assumes intro2 is plain text in translations)
        const htmlToPlain = (html) => {
          if (!html) return '';
          return String(html).replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
        };

        const metaSelector = 'meta[name="description"]';
        let metaDescription = document.querySelector(metaSelector);
        if (!metaDescription) {
          // Create meta description if it doesn't exist
          metaDescription = document.createElement('meta');
          metaDescription.setAttribute('name', 'description');
          document.getElementsByTagName('head')[0].appendChild(metaDescription);
        }

        let descRaw = t('header.paragraphs.intro2', metaDescription.getAttribute('content') || '');
        let desc = htmlToPlain(descRaw);
        if (desc) metaDescription.setAttribute('content', desc);

        // Set Open Graph tags (og:title, og:description) for social sharing if head exists
        const setOrUpdateMeta = (attrName, attrValue, isProperty = false) => {
          const selector = isProperty ? `meta[property="${attrName}"]` : `meta[name="${attrName}"]`;
          let node = document.head.querySelector(selector);
          if (!node) {
            node = document.createElement('meta');
            if (isProperty) node.setAttribute('property', attrName); else node.setAttribute('name', attrName);
            document.head.appendChild(node);
          }
          node.setAttribute('content', attrValue);
        };

        // Use the same title and description for OG tags unless there are explicit translation keys later
        setOrUpdateMeta('og:title', title, true);
        setOrUpdateMeta('og:description', desc, true);

      } catch (headErr) {
        console.warn('Failed to set document head from translations');
      }

      // Initialize DataLoader with translationManager for locale-specific element files
      this.dataLoader = new DataLoader(this.translationManager);

      // Load all data
      await this.dataLoader.loadAll();

      // Initialize components
      this.tableRenderer = new TableRenderer(this.dataLoader);
      this.filterManager = new FilterManager(this.dataLoader);
      this.infographicManager = new InfographicManager(this.dataLoader, this.filterManager, this.translationManager);
      this.mobileToggle = new MobileFilterToggle();
      this.coreTableManager = new CoreTableManager(this.dataLoader);
      this.backToTopButton = new BackToTopButton();

      // Initialize search managers
      this.tableSearchManager = new TableSearchManager(this.dataLoader, this.filterManager);
      this.coreSearchManager = new CoreSearchManager(this.dataLoader, this.coreTableManager);

      // Initialize element modal manager (must be after coreTableManager)
      this.elementModalManager = new ElementModalManager(this.dataLoader, this.coreTableManager);

      // Initialize share links manager for social media sharing
      this.shareLinksManager = new ShareLinksManager();

      // Initialize keyboard navigation manager for desktop arrow key navigation
      this.keyboardNavigationManager = new KeyboardNavigationManager();

      // Render table
      this.tableRenderer.render();

      // Apply initial filters
      this.filterManager.applyFilters();

      // Initialize core table
      await this.coreTableManager.init();

  console.debug('Periodic Table App initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Periodic Table App:', error);
    }
  }
}

// ============================================================================
// INITIALIZATION
// ============================================================================

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const app = new PeriodicTableApp();
    app.init();
  });
} else {
  const app = new PeriodicTableApp();
  app.init();
}

// Public API for backwards compatibility
window.PTData = {
  loadData: async (callback) => {
    try {
      const translationManager = new TranslationManager();
      await translationManager.load().catch(() => {}); // Ignore translation errors
      const loader = new DataLoader(translationManager);
      const data = await loader.loadAll();
      callback?.(null, data.elements);
    } catch (err) {
      callback?.(err);
    }
  },
  getElements: () => {
    if (window.ptApp?.dataLoader) {
      return window.ptApp.dataLoader.getElements();
    }
    return [];
  }
};
